/** Procedural meshes for the sub, props and enemies. No external assets. */

import {
  MeshBuilder, MAT, revolveX, capsuleX, box, cylinderY, sphere, plate,
  type MeshData,
} from './mesh';
import { Rng } from '../core/noise';

const HULL_LEN = 7.2;

/** The player submersible: hull, sail, fins, nacelles, lamp housing. */
/**
 * The PATHFINDER, built from the segmented reference.
 *
 * Every number below is a measured fraction of hull length taken off a
 * part-segmented scan — 79 parts grouped into 28, each with a bounding box, a
 * position along the hull and a base colour that maps onto a `MAT` tag. That
 * last part is why the model is worth more segmented than whole: an unnamed
 * mesh gives a grey lump, and this gives a build sheet.
 *
 * THE THING THAT WAS MISSING IS THE DORSAL CASING. The reference's whole
 * character is a compound curve along its top — a long low fairing rising out
 * of the pressure hull with the sail growing out of THAT — and the previous
 * pass merged the two into a naked box sitting on a bare tube. Side by side it
 * was the only difference that mattered: a straight line with a box on it
 * against a curve that flows from bow to sail to stern.
 *
 * Also corrected against measurement: the hull was 10 % too fat (0.121 L against
 * 0.110), too round (superellipse 2.5 against a fitted 3.12), and squashed the
 * WRONG WAY — 0.86 makes it narrower than tall where the reference is very
 * slightly wider (1.03). The saddle pods sat at |z| 0.10 L INSIDE a 0.121 L
 * hull, so they were buried; they carry the beam and belong proud at 0.144,
 * which is the difference between W/L 0.249 and the reference's 0.373.
 */
export function buildSub(): MeshData {
  const mb = new MeshBuilder();
  const L = HULL_LEN;
  /** Fraction of length -> world units. */
  const F = (v: number) => v * L;
  /** Station t (0 = stern tip, 1 = bow tip) -> world x. */
  const at = (t: number) => (t - 0.5) * L;

  // ── pressure hull ───────────────────────────────────────────────────────
  //
  // Spans t 0.135..0.901 only: the stern 13.5 % is duct and fairing, the bow
  // 10 % is the lamp assembly. The taper is ASYMMETRIC — long and fine to the
  // stern, short to a fine bow that ends at r 0.04 L rather than a fat cap,
  // which is what the bell then stands off.
  mb.material(MAT.paint);
  revolveX(mb, (t) => {
    // Remap 0..1 onto the hull's own span.
    const u = 0.135 + t * (0.901 - 0.135);
    const x = at(u);
    let r: number;
    if (u < 0.345) {
      // Stern taper, fine at the shaft and swelling into the parallel body.
      const k = (u - 0.135) / 0.210;
      r = 0.038 + Math.sqrt(k) * (0.105 - 0.038);
    } else if (u < 0.75) {
      r = 0.105 + Math.sin((u - 0.345) / 0.405 * Math.PI) * 0.006;
    } else {
      // Bow round-off: 0.104 -> 0.041 over the last 15 %.
      const k = (u - 0.75) / 0.151;
      r = 0.104 - k * k * 0.063;
    }
    return [x, Math.max(0.01, F(r))];
  }, 34, 26, 3.12, 1.03);

  // ── dorsal casing ───────────────────────────────────────────────────────
  //
  // The single most valuable shape here. A low rounded fairing lying along the
  // spine, t 0.415..0.752, that the sail rises out of.
  mb.material(MAT.paint);
  mb.transform(at(0.583), F(0.100), 0, 0, 0, 0, 1, 1, 1);
  revolveX(mb, (t) => {
    const x = (t - 0.5) * F(0.337);
    // Fairs into the hull at both ends rather than stopping dead.
    const r = F(0.072) * Math.sqrt(Math.max(0, 1 - Math.pow(Math.abs(t - 0.5) * 2, 2.6)));
    return [x, Math.max(0.01, r)];
  }, 20, 20, 2.8, 1.0);
  mb.popTransform();
  // Coaming rail along the top of the casing.
  mb.material(MAT.metal);
  mb.transform(at(0.620), F(0.169), 0, 0, 0, 0, 1, 1, 1);
  box(mb, F(0.097), F(0.013), F(0.057));
  mb.popTransform();
  // Blister just aft of the sail.
  mb.material(MAT.paint);
  mb.transform(at(0.462), F(0.169), 0, 0, 0, 0, 1, 0.44, 1.7);
  sphere(mb, F(0.023), 12, 8);
  mb.popTransform();

  // ── sail, growing out of the casing ─────────────────────────────────────
  mb.material(MAT.metal);
  mb.transform(at(0.596), F(0.194), 0, 0, 0, 0, 1, 1, 1);
  box(mb, F(0.112), F(0.030), F(0.055));
  mb.popTransform();
  mb.transform(at(0.617), F(0.233), 0, 0, 0, 0, 1, 1, 1);
  box(mb, F(0.027), F(0.011), F(0.027));
  mb.popTransform();
  mb.transform(at(0.656), F(0.283), 0);
  cylinderY(mb, F(0.012), F(0.010), F(0.123), 8);
  mb.popTransform();

  // ── bow: the bell ───────────────────────────────────────────────────────
  //
  // A flared trumpet reflector, not a stub. 0.076 L long opening to 0.123 L
  // across, off a hull that has already tapered to 0.041 — the most
  // recognisable single feature on the boat.
  mb.material(MAT.metal);
  mb.transform(at(0.912), F(0.015), 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, F(0.036), F(0.030), F(0.028), 14);
  mb.popTransform();
  mb.material(MAT.paint);
  mb.transform(at(0.962), F(0.016), 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, F(0.0615), F(0.030), F(0.076), 16, false);
  mb.popTransform();
  // The lens, recessed at the bell's throat.
  mb.material(MAT.glow);
  mb.transform(at(0.928), F(0.016), 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, F(0.0235), F(0.0235), F(0.004), 12);
  mb.popTransform();
  // Chin fairing under the bow.
  mb.material(MAT.metal);
  mb.transform(at(0.860), F(-0.041), 0, 0, 0, 0, 1.1, 0.55, 1.9);
  sphere(mb, F(0.036), 12, 8);
  mb.popTransform();

  // ── stern: ducted propeller ─────────────────────────────────────────────
  mb.material(MAT.metal);
  mb.transform(at(0.027), 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, F(0.0765), F(0.0765), F(0.054), 18, false);
  mb.popTransform();
  // Four blades inside it, so the duct reads as a propeller and not a ring.
  for (let i = 0; i < 4; i++) {
    mb.transform(at(0.027), 0, 0, (i / 4) * Math.PI * 2, 0, 0, 1, 1, 1);
    box(mb, F(0.006), F(0.062), F(0.016));
    mb.popTransform();
  }
  mb.material(MAT.drive);
  mb.transform(at(0.045), 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, F(0.030), F(0.018), F(0.012), 12);
  mb.popTransform();
  // Long fine shaft fairing from the hull into the duct.
  mb.material(MAT.metal);
  mb.transform(at(0.137), 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, F(0.014), F(0.032), F(0.112), 12);
  mb.popTransform();

  // ── stern fins: one dorsal, one ventral, raked ──────────────────────────
  mb.material(MAT.metal);
  mb.transform(at(0.127), F(0.088), 0, 0, 0, 0.30, 1, 1, 1);
  box(mb, F(0.095), F(0.076), F(0.017));
  mb.popTransform();
  mb.transform(at(0.128), F(-0.083), 0, 0, 0, -0.30, 1, 1, 1);
  box(mb, F(0.095), F(0.066), F(0.017));
  mb.popTransform();

  // ── saddle pods, PROUD of the hull ──────────────────────────────────────
  //
  // Half-width 0.186 L against a hull of 0.110 — they add 65 % to the beam and
  // are most of the boat's width. Buried inside the hull they did nothing.
  for (const z of [-1, 1]) {
    mb.material(MAT.metal);
    mb.transform(at(0.430), F(-0.019), z * F(0.144), 0, 0, 0, 1, 1, 1);
    capsuleX(mb, F(0.145), F(0.047), 14, 10);
    mb.popTransform();
    // Bright nose cap, as on the reference.
    mb.material(MAT.paint);
    mb.transform(at(0.556), F(-0.019), z * F(0.144), 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, F(0.040), F(0.030), F(0.030), 12);
    mb.popTransform();
  }

  // ── two thick straps ────────────────────────────────────────────────────
  //
  // 0.054 and 0.034 L wide. Three at 0.012 read as wires; strapping is broad.
  mb.material(MAT.metal);
  mb.transform(at(0.360), 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, F(0.114), F(0.114), F(0.054), 22, false);
  mb.popTransform();
  mb.transform(at(0.648), 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, F(0.113), F(0.113), F(0.034), 22, false);
  mb.popTransform();
  // A thin frame rib, for one more line without weight.
  mb.transform(at(0.394), 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, F(0.113), F(0.113), F(0.015), 20, false);
  mb.popTransform();

  // ── flank details ───────────────────────────────────────────────────────
  for (const z of [-1, 1]) {
    mb.material(MAT.glow);
    mb.transform(at(0.470), F(0.066), z * F(0.108), 0, 0, 0, 1, 1, 1);
    box(mb, F(0.036), F(0.013), F(0.006));
    mb.popTransform();
    mb.material(MAT.paint);
    mb.transform(at(0.577), F(0.034), z * F(0.112), 0, 0, 0, 1, 1, 1);
    box(mb, F(0.048), F(0.042), F(0.007));
    mb.popTransform();
    mb.material(MAT.metal);
    mb.transform(at(0.692), F(0.035), z * F(0.118), 0, 0, 0, 1, 1, 1);
    box(mb, F(0.056), F(0.012), F(0.014));
    mb.popTransform();
  }

  // ── skid frame ──────────────────────────────────────────────────────────
  //
  // Rails and stanchions under the hull. Reads as "this thing sets down on the
  // bottom", which no single primitive says on its own.
  mb.material(MAT.metal);
  for (const z of [-1, 1]) {
    mb.transform(at(0.521), F(-0.100), z * F(0.062), 0, 0, 0, 1, 1, 1);
    capsuleX(mb, F(0.187), F(0.011), 8, 6);
    mb.popTransform();
    for (const t of [0.372, 0.666]) {
      mb.transform(at(t), F(-0.070), z * F(0.062), 0, 0, 0, 1, 1, 1);
      box(mb, F(0.008), F(0.030), F(0.008));
      mb.popTransform();
    }
  }
  // Keel strip aft.
  mb.transform(at(0.302), F(-0.098), 0, 0, 0, 0, 1, 1, 1);
  box(mb, F(0.055), F(0.019), F(0.021));
  mb.popTransform();

  return mb.build();
}

/**
 * The DRAY: a heavy recovery tender.
 *
 * Read as a working boat rather than a fast one. Where the Pathfinder is an
 * ogive with fins, this is a slab-sided hull with a flat working deck, an open
 * gantry over the stern and a winch drum you can actually see the cable coming
 * off. The silhouette has to say "this thing lifts" from across a corridor,
 * because that is the only reason to own one.
 */
export function buildDray(): MeshData {
  const mb = new MeshBuilder();
  const L = 7.6;

  // Hull: blunt bow, parallel body, square transom. Nothing tapers to a point.
  mb.material(MAT.paint);
  revolveX(mb, (t) => {
    const x = -L * 0.5 + t * L;
    let r: number;
    if (t < 0.18) r = 0.75 + (t / 0.18) * 0.75;
    else if (t < 0.78) r = 1.5;
    else r = 1.5 * (1 - (t - 0.78) / 0.22 * 0.28);
    return [x, Math.max(0.05, r)];
  }, 26, 18);

  // Flat working deck along the top, which is what stops it reading as a tube.
  mb.material(MAT.metal);
  mb.transform(-0.3, 1.35, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 2.5, 0.16, 1.45);
  mb.popTransform();

  // Bridge, set well forward and boxy.
  mb.material(MAT.paint);
  mb.transform(1.35, 1.95, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 0.95, 0.65, 1.0);
  mb.popTransform();
  mb.material(MAT.glow);
  mb.transform(2.2, 2.05, 0, 0, 0, 0, 0.1, 0.34, 0.8);
  box(mb, 1, 1, 1);
  mb.popTransform();

  // The gantry: two legs and a beam over the stern, where the cable comes off.
  mb.material(MAT.metal);
  for (const z of [-1.15, 1.15]) {
    mb.transform(-2.0, 1.5, z, 0, 0, 0.12, 1, 1, 1);
    box(mb, 0.13, 0.95, 0.13);
    mb.popTransform();
  }
  mb.transform(-2.2, 2.4, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 0.16, 0.14, 1.3);
  mb.popTransform();

  // Winch drum, athwartships under the gantry.
  mb.transform(-1.5, 0.5, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.62, 0.62, 1.9, 14);
  mb.popTransform();
  for (const z of [-0.95, 0.95]) {
    mb.transform(-1.5, 0.5, z, 0, 0, 0, 1, 1, 1);
    cylinderY(mb, 0.78, 0.78, 0.16, 14, false);
    mb.popTransform();
  }

  // Ballast blisters low on each side — where the lift capacity lives.
  for (const z of [-1.25, 1.25]) {
    mb.transform(-0.2, -0.85, z, 0, 0, 0, 1, 0.7, 0.7);
    capsuleX(mb, 2.1, 0.62, 12, 10);
    mb.popTransform();
  }

  // Twin heavy nacelles with recessed bells.
  for (const z of [-1.5, 1.5]) {
    mb.material(MAT.metal);
    mb.transform(-1.6, -0.2, z);
    revolveX(mb, (t) => {
      const x = -1.15 + t * 2.3;
      const r = t < 0.78 ? 0.46 : 0.46 * (1 - (t - 0.78) / 0.22 * 0.5);
      return [x, r];
    }, 12, 12);
    mb.popTransform();
    mb.material(MAT.drive);
    mb.transform(-2.78, -0.2, z, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 0.4, 0.22, 0.4, 14);
    mb.popTransform();
  }

  // Work lamps: a big lens under the bow, plus deck floods.
  mb.material(MAT.metal);
  mb.transform(2.95, -0.4, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.6, 0.52, 0.6, 14);
  mb.popTransform();
  mb.material(MAT.glow);
  mb.transform(3.28, -0.4, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.5, 0.5, 0.07, 14);
  mb.popTransform();
  for (const z of [-1.0, 1.0]) {
    mb.transform(0.2, 1.6, z, 0, 0, 0, 1, 1, 1);
    box(mb, 0.14, 0.12, 0.14);
    mb.popTransform();
  }

  // Hull frames, for silhouette.
  mb.material(MAT.metal);
  for (const x of [-1.0, 0.2, 1.4]) {
    mb.transform(x, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 1.55, 1.55, 0.12, 18, false);
    mb.popTransform();
  }

  return mb.build();
}

/** Propeller, drawn as a separate instance so it can spin. */
export function buildProp(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  mb.transform(0, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.26, 0.18, 0.5, 12);
  mb.popTransform();
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    mb.transform(0, Math.cos(a) * 0.62, Math.sin(a) * 0.62, a, 0.42, 0, 1, 1, 1);
    plate(mb, 0.16, 0.5);
    mb.popTransform();
  }
  // Shroud ring.
  mb.transform(0, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 1.02, 1.02, 0.5, 20, false);
  mb.popTransform();
  return mb.build();
}

export function buildTorpedo(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  revolveX(mb, (t) => {
    const x = -0.95 + t * 1.9;
    const r = t > 0.72 ? 0.22 * Math.sqrt(Math.max(0, 1 - Math.pow((t - 0.72) / 0.28, 2))) : 0.22;
    return [x, Math.max(0.02, r)];
  }, 14, 12);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    mb.transform(-0.8, Math.cos(a) * 0.3, Math.sin(a) * 0.3, a, 0, 0, 1, 1, 1);
    plate(mb, 0.22, 0.2);
    mb.popTransform();
  }
  mb.material(MAT.glow);
  mb.transform(-1.0, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.16, 0.16, 0.06, 10);
  mb.popTransform();
  return mb.build();
}

export function buildBullet(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.glow);
  revolveX(mb, (t) => [-0.7 + t * 1.4, 0.16 * Math.sin(t * Math.PI) + 0.02], 10, 8);
  return mb.build();
}

/** Moored contact mine: spherical body with horns. */
export function buildMine(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  // 24 x 16, not 18 x 12. A smooth metal ball is the one shape that shows the
  // interpolated-normal crease at every quad edge — the same C1 problem that
  // made the chrome reference sphere look faceted (see `buildProbe`).
  //
  // Sized to the LOBE, not to a mirror. Metal sits at 0.38 roughness, about a
  // 22 degree lobe, so 360/24 = 15 degree facets disappear inside it. The
  // reference ball needed 128 because chrome at 0.05 has a 3 degree lobe;
  // matching that here would be 5x the triangles for a difference no metal
  // surface can show.
  //
  // 768 triangles against 432, at a capacity of 96 — so 74k worst case rather
  // than 41k. That is the real cost and it is why this is 24 and not 64. A
  // distance LOD would make the question go away; there is not one.
  sphere(mb, 1.25, 24, 16);
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const el = i % 3 === 0 ? 0.9 : 0.25;
    const cx = Math.cos(a) * Math.cos(el) * 1.2;
    const cy = Math.sin(el) * 1.2;
    const cz = Math.sin(a) * Math.cos(el) * 1.2;
    mb.transform(cx, cy, cz, 0, 0, -Math.atan2(cx, cy), 1, 1, 1);
    cylinderY(mb, 0.11, 0.07, 0.62, 6);
    mb.popTransform();
  }
  mb.material(MAT.glow);
  mb.transform(0, 0, 1.15, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.2, 0.16, 0.16, 10);
  mb.popTransform();
  return mb.build();
}

/** Small mechanical attack drone. */
export function buildDrone(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.paint);
  mb.transform(0, 0, 0, 0, 0, 0, 1, 0.8, 0.9);
  revolveX(mb, (t) => {
    const x = -1.5 + t * 3.0;
    const r = 0.85 * Math.sin(Math.pow(t, 0.75) * Math.PI) + 0.08;
    return [x, r];
  }, 18, 16);
  mb.popTransform();
  mb.material(MAT.metal);
  // Twin ducted rotors.
  for (const z of [-0.95, 0.95]) {
    mb.transform(-0.5, 0.1, z, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 0.42, 0.42, 0.5, 12, false);
    mb.popTransform();
    mb.transform(-0.5, 0.1, z, 0, 0, 0, 1, 1, 1);
    box(mb, 0.06, 0.06, 0.5);
    mb.popTransform();
  }
  // Dorsal blade.
  mb.transform(-0.35, 0.72, 0, 0, 0, 0.1, 1, 1, 1);
  box(mb, 0.55, 0.32, 0.05);
  mb.popTransform();
  // Sensor eye.
  mb.material(MAT.glow);
  mb.transform(1.3, 0.05, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.3, 0.22, 0.14, 12);
  mb.popTransform();
  return mb.build();
}

/** The wrecked submarine landmark (PRD §31). */
export function buildWreck(): MeshData {
  const mb = new MeshBuilder();
  const rng = new Rng(99173);
  mb.material(MAT.metal);
  // Broken hull, split into a bow and a stern lying at different angles.
  mb.transform(-6, 0.6, 0, 0.1, 0, 0.22, 1, 1, 1);
  revolveX(mb, (t) => {
    const x = -8 + t * 16;
    const r = t > 0.86 ? 2.6 * (1 - (t - 0.86) / 0.14) : 2.6 * (0.72 + 0.28 * Math.sin(t * 3.0));
    return [x, Math.max(0.05, r)];
  }, 26, 18);
  mb.popTransform();

  mb.transform(12.5, -0.4, 0.5, -0.06, 0, -0.42, 1, 1, 1);
  revolveX(mb, (t) => {
    const x = -6 + t * 12;
    const r = t < 0.12 ? 2.5 * (t / 0.12) : 2.5 * (1 - Math.pow((t - 0.12) / 0.88, 2.6));
    return [x, Math.max(0.05, r)];
  }, 22, 18);
  mb.popTransform();

  // Collapsed sail.
  mb.transform(-4.5, 2.6, -0.4, 0.2, 0.3, 0.5, 1, 1, 1);
  box(mb, 2.2, 1.5, 0.9);
  mb.popTransform();

  // Torn plating scattered around.
  for (let i = 0; i < 9; i++) {
    const x = rng.range(-16, 20);
    const y = rng.range(-1.6, 1.2);
    const z = rng.range(-3.5, 3.5);
    mb.transform(x, y, z, rng.range(-1, 1), rng.range(-1, 1), rng.range(-1, 1), 1, 1, 1);
    box(mb, rng.range(0.6, 2.2), 0.09, rng.range(0.5, 1.8));
    mb.popTransform();
  }

  // Exposed ribs where the hull broke.
  for (let i = 0; i < 5; i++) {
    mb.transform(2.5 + i * 1.4, 0.2, 0, 0, 0, Math.PI / 2 + rng.range(-0.2, 0.2), 1, 1, 1);
    cylinderY(mb, 2.3 - i * 0.16, 2.3 - i * 0.16, 0.16, 16, false);
    mb.popTransform();
  }

  // A single light still burning inside.
  mb.material(MAT.glow);
  mb.transform(1.5, 0.4, 1.4);
  sphere(mb, 0.26, 10, 6);
  mb.popTransform();
  return mb.build();
}

/**
 * Kelp blade: a tapered ribbon with a built-in lean and twist so a field of
 * them never looks like a row of posts. Instances add per-blade sway.
 */
/**
 * A kelp blade. `lod` 1 is the distant/silhouette build.
 *
 * The three gas bladders were 240 of this mesh's 268 triangles — a sphere at
 * (8, 5) each, for a float 3.5 cm across. A kelp forest puts nine hundred
 * blades on screen at once, so almost a quarter of a million triangles a frame
 * were being spent on beads the size of a pixel. They keep enough tessellation
 * to stay round at arm's length and lose it entirely at distance.
 */
export function buildKelp(lod = 0): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.leaf);
  const segs = lod ? 6 : 14;
  const h = 1 / segs;
  const curve = (y: number) => y * y * 0.42;                  // leans downstream
  const dCurve = (y: number) => y * 0.84;                     // ...and its slope
  const twist = (y: number) => Math.sin(y * 3.4) * 0.22;      // ribbon roll
  const width = (y: number) => (0.055 + Math.sin(y * Math.PI) * 0.085) * (1 - y * 0.35);
  /**
   * The blade's real normal, which it did not have: every vertex was handed a
   * flat (0, 0, 1) while the ribbon rolled through `twist`. Both faces claimed
   * to point at the camera, so the roll caught no light and a blade read as a
   * paper cutout that thinned to a sliver wherever it turned edge-on.
   *
   * The ribbon is ruled, so across it the normal is constant and exact: the
   * span is (2w, 0, 2z) and the stipe runs along (curve'(y), 1, 0); their
   * cross product is this. Untwisted it returns (0, 0, 1), which is what the
   * old constant was trying to be.
   */
  const normalAt = (y: number): [number, number, number] => {
    const w = width(y);
    const z = twist(y) * w * 3;
    const nx = -z, ny = z * dCurve(y), nz = w;
    const l = Math.hypot(nx, ny, nz) || 1;
    return [nx / l, ny / l, nz / l];
  };
  for (let i = 0; i < segs; i++) {
    const y0 = i * h, y1 = (i + 1) * h;
    const w0 = width(y0), w1 = width(y1);
    const x0 = curve(y0), x1 = curve(y1);
    const z0 = twist(y0) * w0 * 3, z1 = twist(y1) * w1 * 3;
    const [ax, ay, az] = normalAt(y0);
    const [bx, by, bz] = normalAt(y1);
    const b = mb.vertexCount;
    mb.vertex(x0 - w0, y0, -z0, ax, ay, az, 0, y0);
    mb.vertex(x0 + w0, y0, z0, ax, ay, az, 1, y0);
    mb.vertex(x1 + w1, y1, z1, bx, by, bz, 1, y1);
    mb.vertex(x1 - w1, y1, -z1, bx, by, bz, 0, y1);
    mb.quad(b, b + 1, b + 2, b + 3);
  }
  // A few gas bladders along the stipe. Dropped entirely on the far build:
  // background kelp is a near-black silhouette and a bead on it is invisible.
  if (!lod) {
    for (let i = 1; i < 4; i++) {
      const y = i / 4.2;
      mb.transform(curve(y), y, 0, 0, 0, 0, 0.035, 0.05, 0.035);
      sphere(mb, 1, 5, 3);
      mb.popTransform();
    }
  }
  return mb.build();
}

/** Coral fan / rock weed clump. */
/**
 * A coral head. `lod` 1 is the far version.
 *
 * Rock and kelp have had one of these since they were written and coral never
 * did, which mattered little while it was a rare brown thing at the back of a
 * few zones. It is now the palette of the whole dive and there are twice as
 * many of them, so at 624 triangles a head a garden was most of a frame.
 *
 * Fewer arms and coarser revolves rather than a different shape: the far one
 * has to read as the same species as the one beside it, and a coral head is a
 * silhouette of branches — take two branches away at thirty metres and nobody
 * can say which two.
 */
/**
 * A flat unit square, for the refit screen to stand its hull against.
 *
 * TWO TRIANGLES OF NOTHING, and they earn their place. The display hull is drawn in
 * the world so it can be posed cheaply, which also means it is drawn against
 * whatever sea happens to be outside the station — a bright teal wall at a
 * shallow berth. Read against that, a hull has to be BRIGHT to be legible, and
 * bright is what trips the bloom threshold, so the model wore a halo and the
 * panel looked hazy.
 *
 * A black card behind it inverts the problem: the hull reads by contrast
 * instead of by output, so it can sit well under the threshold and still be the
 * clearest thing on the screen. Which is what a photographer would have reached
 * for in the first place.
 *
 * A black tint and no specular amount render it as actual black — every
 * lighting term in `fsMesh` is multiplied by either albedo or `emis.w`.
 *
 * ORGANIC, NOT PAINT, and the difference is the whole point of the card.
 * `MAT.paint` means lacquer: `wPaint` comes out at 1, which is a clear coat, a
 * roughness of 0.12 and a reflectivity of 0.34. That is piano black, and the
 * screen-space reflections duly put the submarine in it — a bright smear under
 * the model, which then cleared the bloom threshold on its own and produced
 * the exact halo this card was added to remove.
 *
 * Organic is the matte end of the material table: reflectivity 0.051,
 * roughness 0.55, no coat and no metal. Studio felt rather than a showroom
 * floor. Its translucency term is harmless here — it is driven by albedo and
 * by bioluminescence, and this has neither.
 */
export function buildBackdrop(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.organic);
  // Facing +Z, which is the camera in this game.
  mb.vertex(-1, -1, 0, 0, 0, 1, 0, 0);
  mb.vertex(1, -1, 0, 0, 0, 1, 1, 0);
  mb.vertex(1, 1, 0, 0, 0, 1, 1, 1);
  mb.vertex(-1, 1, 0, 0, 0, 1, 0, 1);
  mb.quad(0, 1, 2, 3);
  return mb.build();
}

export function buildCoral(lod = 0): MeshData {
  const mb = new MeshBuilder();
  const rng = new Rng(4471);
  mb.material(MAT.leaf);
  const far = lod > 0;
  const arms = far ? 5 : 7;
  for (let i = 0; i < arms; i++) {
    const a = (i / arms) * Math.PI * 2;
    const lean = rng.range(-0.5, 0.5);
    mb.transform(Math.cos(a) * 0.2, 0, Math.sin(a) * 0.2, 0, a, lean, 1, 1, 1);
    revolveX(mb, (t) => [t * 1.0, 0.11 * (1 - t) + 0.012], far ? 4 : 6, far ? 3 : 6);
    mb.popTransform();
  }
  mb.transform(0, 0.08, 0, 0, 0, 0, 1, 0.6, 1);
  sphere(mb, 0.3, far ? 6 : 10, far ? 4 : 6);
  mb.popTransform();
  return mb.build();
}

/** Bioluminescent jelly — background life (PRD §2.3). */
export function buildJelly(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.glow);
  revolveX(mb, (t) => {
    const y = t;
    const r = Math.sin(Math.pow(t, 0.6) * Math.PI * 0.5) * 0.6;
    return [-y * 0.9 + 0.45, Math.max(0.02, r)];
  }, 14, 14);
  mb.material(MAT.organic);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    mb.transform(-0.45, Math.cos(a) * 0.3, Math.sin(a) * 0.3, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 0.05, 0.01, 1.4, 5, false);
    mb.popTransform();
  }
  return mb.build();
}


/** A boulder used for foreground silhouettes. */
/**
 * `lod` 1 drops a boulder from 768 triangles to 120.
 *
 * There was one rock mesh and everything used it: play-layer boulders,
 * foreground silhouettes, background clumps on both parallax layers, coral
 * bommies and every scatter call. A pebble two metres across and eighty metres
 * behind the play plane was being drawn at the same density as the one you can
 * bump into, which is most of the geometry in a rocky frame.
 */
export function buildRock(seed: number, lod = 0): MeshData {
  const mb = new MeshBuilder();
  const rng = new Rng(seed);
  mb.material(MAT.organic);
  const seg = lod ? 10 : 24, rings = lod ? 6 : 16;
  const base = mb.vertexCount;
  const jitter: number[] = [];
  for (let r = 0; r <= rings; r++) {
    for (let i = 0; i <= seg; i++) {
      jitter.push(i === seg ? jitter[jitter.length - seg] : rng.range(0.84, 1.16));
    }
  }
  let k = 0;
  for (let r = 0; r <= rings; r++) {
    const phi = (r / rings) * Math.PI;
    const y = Math.cos(phi), rr = Math.sin(phi);
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const nx = Math.cos(a) * rr, nz = Math.sin(a) * rr;
      const j = jitter[k++];
      mb.vertex(nx * j, y * j * 0.8, nz * j, nx, y, nz, i / seg, r / rings);
    }
  }
  const stride = seg + 1;
  for (let r = 0; r < rings; r++) {
    for (let i = 0; i < seg; i++) {
      const a = base + r * stride + i;
      mb.quad(a, a + stride, a + stride + 1, a + 1);
    }
  }
  return mb.build();
}

/** Octopus mantle: a soft bulb with a heavy brow and two eyes. */
export function buildOctoHead(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.organic);
  mb.transform(0, 0, 0, 0, 0, 0, 1, 1.18, 0.86);
  revolveX(mb, (t) => {
    const x = -1.15 + t * 2.35;
    const r = Math.sin(Math.pow(t, 0.62) * Math.PI) * 1.05 + 0.05;
    return [x, r];
  }, 26, 22);
  mb.popTransform();

  // Brow ridge and mantle folds.
  for (let i = 0; i < 3; i++) {
    const x = -0.35 + i * 0.42;
    mb.transform(x, 0.18, 0, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 0.94 - i * 0.14, 0.94 - i * 0.14, 0.14, 20, false);
    mb.popTransform();
  }

  // Siphon.
  mb.transform(-0.95, -0.4, 0.35, 0, 0.5, -0.5, 1, 1, 1);
  revolveX(mb, (t) => [t * 0.9, 0.24 * (1 - t * 0.5)], 6, 8);
  mb.popTransform();

  // Eyes — the lockable weak point.
  mb.material(MAT.glow);
  for (const z of [-0.58, 0.58]) {
    mb.transform(0.56, 0.30, z, 0, 0, 0, 0.19, 0.15, 0.17);
    sphere(mb, 1, 14, 9);
    mb.popTransform();
  }
  return mb.build();
}

/** One tentacle segment, tapered along +X and drawn many times per limb. */
export function buildTentacleSeg(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.organic);
  revolveX(mb, (t) => [t - 0.5, 0.5 + Math.sin(t * Math.PI) * 0.12], 8, 12);
  // Suckers along the underside.
  for (let i = 0; i < 3; i++) {
    const x = -0.32 + i * 0.32;
    mb.transform(x, -0.42, 0, 0, 0, 0, 0.12, 0.12, 0.12);
    sphere(mb, 1, 7, 4);
    mb.popTransform();
  }
  return mb.build();
}

/**
 * A cargo container.
 *
 * Deliberately a ribbed box with corner castings rather than anything more
 * interesting. It has to read as *mass* from across a corridor, and its
 * silhouette has to be unmistakably neither organic nor a pickup canister —
 * everything else in the water is round. Eight boxes, 96 triangles.
 */
/**
 * Cargo, in five shapes.
 *
 * There are sixteen named things in this game that can go on the winch — CORAL
 * BLOOM, ANCHOR STONE, VENT TURBINE, REACTOR CORE, SENSOR CELL, ANCIENT MASS,
 * SEALED RELIQUARY and Expedition's six — and every one of them was the same
 * steel container. Worse, they were the same SIZE: mass runs 0.35 t to 4.6 t, a
 * thirteenfold spread, and `0.85 * cbrt(mass)` compresses that into 2.3x of
 * visible scale, so the heaviest thing in the world looked like the lightest.
 *
 * The shape is chosen from the label, because the labels were already doing the
 * work — a drum is a drum and a slab is a slab, and the writing said so long
 * before the mesh did.
 */
export type CargoShape = 'crate' | 'drum' | 'core' | 'slab' | 'pod';

/** Which mesh a piece of cargo uses, from what it is called. */
export function cargoShape(label: string): CargoShape {
  const s = label.toUpperCase();
  if (/DRUM|CELL|SENSOR|REPOSITORY|BARREL/.test(s)) return 'drum';
  if (/CORE|REACTOR|SLUG|TURBINE|SAMPLE/.test(s)) return 'core';
  if (/ANCHOR|PLATE|STONE|MASS|ANCIENT/.test(s)) return 'slab';
  if (/POD|BLOOM|SURVEY|RELIQUARY/.test(s)) return 'pod';
  return 'crate';
}

/** A ribbed barrel, strapped at both ends. */
export function buildDrum(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  cylinderY(mb, 0.86, 0.86, 2.0, 14);
  // Rolling hoops, which is what stops a drum reading as a plain cylinder.
  for (const y of [-0.62, 0, 0.62]) {
    mb.transform(0, y, 0, 0, 0, 0, 1, 1, 1);
    cylinderY(mb, 0.94, 0.94, 0.16, 14, false);
    mb.popTransform();
  }
  mb.material(MAT.paint);
  for (const y of [-1.0, 1.0]) {
    mb.transform(0, y, 0, 0, 0, 0, 1, 1, 1);
    cylinderY(mb, 0.80, 0.80, 0.12, 14);
    mb.popTransform();
  }
  return mb.build();
}

/** A shielded flask: heavy collar, glowing window. */
export function buildCore(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  cylinderY(mb, 0.66, 0.66, 1.7, 12);
  for (const y of [-0.78, 0.78]) {
    mb.transform(0, y, 0, 0, 0, 0, 1, 1, 1);
    cylinderY(mb, 0.92, 0.78, 0.34, 12);
    mb.popTransform();
  }
  // The window. This is the one piece of cargo that says what is inside it.
  mb.material(MAT.glow);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    mb.transform(Math.cos(a) * 0.60, 0, Math.sin(a) * 0.60, 0, a, 0, 1, 1, 1);
    box(mb, 0.10, 0.42, 0.26);
    mb.popTransform();
  }
  return mb.build();
}

/** A dressed stone slab, banded for lifting. */
export function buildSlab(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.organic);
  box(mb, 1.35, 0.46, 0.95);
  // Chamfer, so it reads as cut stone rather than as a brick.
  mb.transform(0, 0.44, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 1.16, 0.10, 0.80);
  mb.popTransform();
  mb.transform(0, -0.44, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 1.16, 0.10, 0.80);
  mb.popTransform();
  mb.material(MAT.metal);
  for (const x of [-0.72, 0.72]) {
    mb.transform(x, 0, 0, 0, 0, 0, 1, 1, 1);
    box(mb, 0.10, 0.56, 1.02);
    mb.popTransform();
  }
  return mb.build();
}

/** A pressure pod: a capsule in a cradle. */
export function buildPod(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.paint);
  capsuleX(mb, 0.72, 0.62, 14, 8);
  mb.material(MAT.metal);
  // Cradle bands, so it has somewhere a hook could plausibly go.
  for (const x of [-0.34, 0.34]) {
    mb.transform(x, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 0.70, 0.70, 0.11, 12, false);
    mb.popTransform();
  }
  mb.transform(0, -0.62, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 0.86, 0.12, 0.34);
  mb.popTransform();
  return mb.build();
}

export function buildCrate(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  mb.transform(0, 0, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 1.5, 1.02, 1.02);
  mb.popTransform();
  // Corner castings — the parts a crane would actually take hold of.
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      mb.transform(sx * 1.44, sy * 0.96, 0, 0, 0, 0, 1, 1, 1);
      box(mb, 0.17, 0.17, 1.09);
      mb.popTransform();
    }
  }
  // Corrugation, three ribs. Enough to catch the lamp and say "steel".
  for (const x of [-0.72, 0, 0.72]) {
    mb.transform(x, 0, 0, 0, 0, 0, 1, 1, 1);
    box(mb, 0.08, 1.0, 1.08);
    mb.popTransform();
  }
  return mb.build();
}

/**
 * A status lamp. Unit radius, so one mesh serves every light on the boat at
 * whatever size the call site wants.
 *
 * Deliberately coarse — eight segments, five rings, 80 triangles. At the size
 * these are drawn a bead is three or four pixels across, and every triangle
 * past that is spent on a silhouette nobody can resolve. What sells them is the
 * emissive and the colour, neither of which costs geometry.
 */
export function buildLed(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.glow);
  sphere(mb, 1, 8, 5);
  return mb.build();
}

/**
 * One link of the tow cable. Unit length along X and radius 0.5, so the world
 * scales it between any two points exactly the way the octopus's tentacle
 * segments are drawn — same trick, same `compose` call shape.
 */
export function buildCableLink(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  // Swaged: fatter at both ends than in the middle, so a chain of them reads as
  // a wound steel cable with joints rather than as a drawn line. Eight sides,
  // three rings — at sixteen links that is under 400 triangles for the whole
  // cable, which is nothing, and it is the difference between "there is a rope
  // here" and a hairline.
  revolveX(mb, (t) => [t - 0.5, 0.34 + Math.abs(t - 0.5) * 1.3], 2, 8);
  return mb.build();
}

/**
 * The hook on the end of the line. Small, but it is the thing the eye follows
 * while the winch is going down, and a line that ends in nothing reads as
 * unfinished.
 */
export function buildHook(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  // Shackle body.
  mb.transform(0, 0, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.34, 0.28, 0.7, 8);
  mb.popTransform();
  // The claw: four bars swept round and inward, which is enough of a suggestion
  // at the size this is ever seen.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    mb.transform(Math.cos(a) * 0.30, -0.52, Math.sin(a) * 0.30, 0, a, 0.5, 1, 1, 1);
    box(mb, 0.09, 0.42, 0.09);
    mb.popTransform();
  }
  // Weight at the top, where the cable enters.
  mb.transform(0, 0.42, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.20, 0.20, 0.26, 8, false);
  mb.popTransform();
  return mb.build();
}

/** Collectible canister (PRD §19). */
export function buildPickup(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  mb.transform(0, 0, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.5, 0.5, 1.5, 12);
  mb.popTransform();
  for (const y of [-0.6, 0.6]) {
    mb.transform(0, y, 0, 0, 0, 0, 1, 1, 1);
    cylinderY(mb, 0.6, 0.6, 0.14, 12, false);
    mb.popTransform();
  }
  mb.material(MAT.glow);
  mb.transform(0, 0.86, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.24, 0.18, 0.24, 10);
  mb.popTransform();
  mb.transform(0, 0, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.52, 0.52, 0.12, 12, false);
  mb.popTransform();
  return mb.build();
}

/** Halo billboard used for explosion cores and muzzle flashes. */
/**
 * The aegis shell — the membrane the field puts around the boat.
 *
 * First attempt was a sphere plus three torus rings on the glow material. It
 * read as a hard bauble with wire hoops round it, hid the sub, and looked
 * nothing like anything else in the water. This is the jellyfish instead.
 *
 * The trick is that it is not a new shader. `scene.wgsl`'s translucent pass
 * dispatches on the material tag, and everything below 0.20 takes the *bell*
 * branch — fresnel-driven, base alpha 0.055, radial canals, a bright margin.
 * A jelly bell is exactly the thing being described here: a membrane that is
 * almost invisible face-on and bright edge-on, which is what "thin" looks like
 * underwater. Tagging the shell into that branch gets the whole treatment for
 * free, and it belongs to the world because it is lit by the same code as the
 * animals in it.
 *
 * The UVs are authored rather than taken from `sphere()`, because the branch
 * reads them: `uv.x` sets how many meridian canals run over the surface (twelve
 * here, and it has to close on a whole number of half-periods or the seam
 * shows), and `uv.y` drives the bright margin — 1 at the equator, 0 at the
 * poles, so the highlight is a band around the boat rather than a puddle under
 * it. Slightly ellipsoidal, to sit along the hull instead of ballooning off it.
 */
export function buildAegisShell(): MeshData {
  const mb = new MeshBuilder();
  // Under 0.20: the bell branch. Not MAT.glow, which is 1.0 and lands in the
  // frilled-oral-arm case.
  mb.material(0.1);
  const seg = 30, rings = 18;
  // Round. It was 1.24 x 0.82, meant to sit along the hull, and it read as a
  // squashed bubble rather than as a field — a force shell is a sphere or it is
  // nothing.
  const RX = 1.0, RY = 1.0, RZ = 1.0;
  const base = mb.vertexCount;
  for (let r = 0; r <= rings; r++) {
    const phi = (r / rings) * Math.PI;
    const y = Math.cos(phi), rr = Math.sin(phi);
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const nx = Math.cos(a) * rr, nz = Math.sin(a) * rr;
      mb.vertex(
        nx * RX, y * RY, nz * RZ,
        nx, y, nz,
        (i / seg) * 0.75,
        1 - Math.abs(y),
      );
    }
  }
  const stride = seg + 1;
  for (let r = 0; r < rings; r++) {
    for (let i = 0; i < seg; i++) {
      const v = base + r * stride + i;
      mb.quad(v, v + stride, v + stride + 1, v + 1);
    }
  }
  return mb.build();
}

export function buildGlowQuad(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.glow);
  plate(mb, 1, 1);
  return mb.build();
}

/**
 * Parametric medusa generator.
 *
 * One recipe, many animals: a spec describes the bell profile, the tentacle
 * curtain and the oral arms, and `jellySpecies(i)` derives a set of distinct,
 * deterministic archetypes from it — the same idea as a geometry-node graph
 * with a seed input, just evaluated once at load and baked into instanced
 * meshes so a field of them still costs one draw call per species.
 *
 * Parts are tagged in the material channel and the length parameter travels in
 * uv.y, so the shader pulses the bell and trails the tentacles with no
 * CPU-side skinning. uv.x carries a per-strand phase, which is what stops the
 * curtain moving as one rigid cage.
 */
export const JELLY_PART = {
  bell: 0.10,
  bloomBell: 0.30,
  bloomSkirt: 0.45,
  tentacleL: 0.60,
  tentacleR: 0.64,
  armL: 0.84,
  armM: 0.87,
  armR: 0.90,
} as const;

export interface JellySpec {
  /** Bell radius and height, in the mesh's own units. */
  bellR: number;
  bellH: number;
  /** <1 gives a flatter, saucer-like dome; >1 a taller bullet. */
  bellPower: number;
  /** Number of scallops around the margin. */
  scallops: number;
  scallopDepth: number;
  /** How much the bell flares out into a lip near the rim. */
  lip: number;
  rings: number;
  seg: number;

  strands: number;
  tentLen: [number, number];
  tentWidth: [number, number];
  splay: [number, number];

  arms: number;
  armLen: [number, number];
  armWidth: number;
  frillFreq: number;
}

export const JELLY_SPECIES = 5;

/** Deterministic archetypes, ordered roughly small → large. */
/**
 * Bells are TALLER than they were. Every one of these was between a half and
 * four fifths as tall as it was wide, and the moon jelly at 0.5 read as a
 * saucer rather than an animal — reported as "too thin in height relative to
 * the radius", which is exactly what the numbers said. `bellPower` goes up with
 * the height so the extra rise is a dome and not a cone.
 */
export function jellySpecies(i: number): JellySpec {
  const archetype = i % JELLY_SPECIES;
  switch (archetype) {
    case 0: // moon jelly — wide flat saucer, short fringe
      return {
        bellR: 1.0, bellH: 0.74, bellPower: 0.56, scallops: 24, scallopDepth: 0.02, lip: 0.03,
        rings: 9, seg: 22,
        strands: 22, tentLen: [0.8, 1.6], tentWidth: [0.014, 0.026], splay: [0.2, 0.6],
        arms: 4, armLen: [1.1, 1.5], armWidth: 0.30, frillFreq: 26,
      };
    case 1: // sea nettle — tall bell, long trailing threads and frilled arms
      return {
        bellR: 1.0, bellH: 0.96, bellPower: 0.66, scallops: 16, scallopDepth: 0.038, lip: 0.06,
        rings: 10, seg: 22,
        strands: 16, tentLen: [3.4, 6.8], tentWidth: [0.022, 0.05], splay: [0.4, 1.3],
        arms: 4, armLen: [3.0, 4.4], armWidth: 0.27, frillFreq: 42,
      };
    case 2: // crown jelly — deeply scalloped, medium curtain
      return {
        bellR: 0.9, bellH: 0.90, bellPower: 0.84, scallops: 12, scallopDepth: 0.085, lip: 0.10,
        rings: 10, seg: 24,
        strands: 13, tentLen: [2.0, 3.6], tentWidth: [0.03, 0.055], splay: [0.7, 1.7],
        arms: 6, armLen: [1.6, 2.4], armWidth: 0.20, frillFreq: 30,
      };
    case 3: // deep thimble — small tall bell, a few very long threads
      return {
        bellR: 0.62, bellH: 0.95, bellPower: 1.15, scallops: 10, scallopDepth: 0.03, lip: 0.02,
        rings: 9, seg: 18,
        strands: 7, tentLen: [5.0, 9.0], tentWidth: [0.018, 0.032], splay: [0.15, 0.5],
        arms: 2, armLen: [1.2, 1.8], armWidth: 0.12, frillFreq: 18,
      };
    default: // giant — broad bell, heavy frilled arms
      return {
        bellR: 1.15, bellH: 1.00, bellPower: 0.62, scallops: 20, scallopDepth: 0.05, lip: 0.08,
        rings: 11, seg: 24,
        strands: 19, tentLen: [4.0, 7.5], tentWidth: [0.03, 0.06], splay: [0.5, 1.5],
        arms: 8, armLen: [3.4, 5.0], armWidth: 0.34, frillFreq: 36,
      };
  }
}

export function buildJellyfish(spec: JellySpec, seed = 1): MeshData {
  const mb = new MeshBuilder();
  const rng = new Rng(seed * 2654435761 + 17);
  const { bellR: R, bellH: H } = spec;

  // --- bell -----------------------------------------------------------------
  mb.material(JELLY_PART.bell);
  // Apex at +H, margin at 0. A slight lip near the rim makes it read as a dome
  // with an edge rather than a plain hemisphere.
  const pos = (t: number, a: number): [number, number, number] => {
    const y = Math.cos(t * Math.PI * 0.5) * H;
    let rad = Math.pow(Math.sin(t * Math.PI * 0.5), spec.bellPower) * R;
    rad *= 1 + spec.scallopDepth * Math.sin(a * spec.scallops) * t * t;
    rad *= 1 + spec.lip * Math.sin(t * Math.PI * 1.6);
    return [Math.cos(a) * rad, y, Math.sin(a) * rad];
  };
  const base = mb.vertexCount;
  const { rings, seg } = spec;
  for (let r = 0; r <= rings; r++) {
    const t = r / rings;
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const [x, y, z] = pos(t, a);
      const e = 0.02;
      const [xt, yt, zt] = pos(Math.min(1, t + e), a);
      const [xa, ya, za] = pos(t, a + e);
      const ux = xt - x, uy = yt - y, uz = zt - z;
      const vx = xa - x, vy = ya - y, vz = za - z;
      let nx = uy * vz - uz * vy;
      let ny = uz * vx - ux * vz;
      let nz = ux * vy - uy * vx;
      const l = Math.hypot(nx, ny, nz) || 1;
      nx /= l; ny /= l; nz /= l;
      if (ny < 0 && t < 0.5) { nx = -nx; ny = -ny; nz = -nz; }
      mb.vertex(x, y, z, nx, ny, nz, i / seg, t);
    }
  }
  const stride = seg + 1;
  for (let r = 0; r < rings; r++) {
    for (let i = 0; i < seg; i++) {
      const a = base + r * stride + i;
      mb.quad(a, a + stride, a + stride + 1, a + 1);
    }
  }

  // --- trailing tentacles ---------------------------------------------------
  const tSegs = 15;
  for (let s = 0; s < spec.strands; s++) {
    const a = (s / spec.strands) * Math.PI * 2 + rng.range(-0.09, 0.09);
    const rr = R * rng.range(0.84, 0.97);
    const cx = Math.cos(a) * rr, cz = Math.sin(a) * rr;
    const len = rng.range(spec.tentLen[0], spec.tentLen[1]);
    const w0 = rng.range(spec.tentWidth[0], spec.tentWidth[1]);
    // Each strand carries its own phase so the curtain never moves as a block.
    const strandPhase = rng.next();
    const splay = rng.range(spec.splay[0], spec.splay[1]);
    const curl = rng.range(-0.6, 0.6);
    const bStart = mb.vertexCount;
    for (let i = 0; i <= tSegs; i++) {
      const v = i / tSegs;
      const out = 1 + v * v * splay;
      const x = cx * out + Math.sin(v * 2.6 + s) * curl * v * v;
      const z = cz * out + Math.cos(v * 2.2 + s) * curl * v * v * 0.7;
      const y = -v * len;
      const w = w0 * (1 - v * 0.7);
      mb.material(JELLY_PART.tentacleL);
      mb.vertex(x - w, y, z, 0, 0, 1, strandPhase, v);
      mb.material(JELLY_PART.tentacleR);
      mb.vertex(x + w, y, z, 0, 0, 1, strandPhase, v);
    }
    for (let i = 0; i < tSegs; i++) {
      const q = bStart + i * 2;
      mb.quad(q, q + 1, q + 3, q + 2);
    }
  }

  // --- frilled oral arms ----------------------------------------------------
  const aSegs = 18;
  for (let s = 0; s < spec.arms; s++) {
    const a = (s / spec.arms) * Math.PI * 2 + 0.4;
    const cx = Math.cos(a) * R * 0.16, cz = Math.sin(a) * R * 0.16;
    const len = rng.range(spec.armLen[0], spec.armLen[1]);
    const strandPhase = rng.next();
    const bStart = mb.vertexCount;
    for (let i = 0; i <= aSegs; i++) {
      const v = i / aSegs;
      const w = (spec.armWidth + Math.sin(v * 19) * spec.armWidth * 0.4) *
                (1 - v * 0.5) * (1 - Math.pow(v, 5));
      const x = cx + Math.sin(v * 3.1 + s * 1.7) * 0.42 * v;
      const z = cz + Math.cos(v * 2.6 + s * 1.3) * 0.34 * v;
      const y = -v * len;
      mb.material(JELLY_PART.armL);
      mb.vertex(x - w, y, z - w * 0.35, 0, 0, 1, strandPhase, v);
      mb.material(JELLY_PART.armM);
      mb.vertex(x, y, z, 0, 0, 1, strandPhase, v);
      mb.material(JELLY_PART.armR);
      mb.vertex(x + w, y, z + w * 0.35, 0, 0, 1, strandPhase, v);
    }
    for (let i = 0; i < aSegs; i++) {
      const q = bStart + i * 3;
      mb.quad(q, q + 1, q + 4, q + 3);
      mb.quad(q + 1, q + 2, q + 5, q + 4);
    }
  }

  void spec.frillFreq;
  return mb.build();
}

/**
 * The bloom: a heavier, showier medusa than the scyphozoans.
 *
 * Where those drift, this one jets — the bell squashes flat and flares wide on
 * every contraction, and the whole animal lurches forward with it. The surface
 * is finely ribbed in concentric contours, which is what carries the iridescent
 * banding in the shader; the ribs are real geometry rather than a texture so the
 * silhouette ripples too.
 */
export function buildBloom(seed = 1): MeshData {
  const mb = new MeshBuilder();
  const rng = new Rng(seed * 40503 + 7);

  const R = 1.0;
  // HEIGHT IS AGAINST THE DIAMETER, NOT THE RADIUS.
  //
  // This is the mistake that made the last two passes at this animal miss. `R`
  // is a radius, so the bell's WIDTH is 2R — and setting H just under R, which
  // sounds like a tall bell, actually builds a hemisphere: 0.96 tall against
  // 2.0 wide is 0.48, and a hemisphere reads as a saucer no matter how the
  // number is written down. The eye compares height to the width it can see.
  //
  // 1.30–1.55 puts the resting bell at about 0.71 of its own width, and the
  // flattest point of the swim stroke at 0.53 — a dome throughout, which is
  // what the reference photographs show.
  const H = rng.range(1.30, 1.55);
  const lobes = rng.int(7, 11);
  const ribs = rng.int(32, 44);
  const rings = 24, seg = 36;

  mb.material(JELLY_PART.bloomBell);
  const pos = (t: number, a: number): [number, number, number] => {
    // Fine concentric corrugation, in both radius and height so the rings
    // catch the light from the side as well as the top.
    const rib = Math.sin(t * ribs) * 0.016;
    let rad = Math.pow(Math.sin(t * Math.PI * 0.5), 0.55) * R * (1 + rib);
    // Lobed margin, growing toward the rim.
    rad *= 1 + 0.06 * Math.sin(a * lobes) * t * t;
    // Exponent 1 is a true dome; above it the profile sags away from the apex
    // and the animal loses the crown that makes it read as a bell.
    const y = Math.cos(t * Math.PI * 0.5) * H + rib * 0.5;
    return [Math.cos(a) * rad, y, Math.sin(a) * rad];
  };

  const base = mb.vertexCount;
  for (let r = 0; r <= rings; r++) {
    const t = r / rings;
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const [x, y, z] = pos(t, a);
      const e = 0.012;
      const [xt, yt, zt] = pos(Math.min(1, t + e), a);
      const [xa, ya, za] = pos(t, a + e);
      const ux = xt - x, uy = yt - y, uz = zt - z;
      const vx = xa - x, vy = ya - y, vz = za - z;
      let nx = uy * vz - uz * vy;
      let ny = uz * vx - ux * vz;
      let nz = ux * vy - uy * vx;
      const l = Math.hypot(nx, ny, nz) || 1;
      nx /= l; ny /= l; nz /= l;
      if (ny < 0 && t < 0.5) { nx = -nx; ny = -ny; nz = -nz; }
      mb.vertex(x, y, z, nx, ny, nz, i / seg, t);
    }
  }
  const stride = seg + 1;
  for (let r = 0; r < rings; r++) {
    for (let i = 0; i < seg; i++) {
      const a = base + r * stride + i;
      mb.quad(a, a + stride, a + stride + 1, a + 1);
    }
  }

  // A short, dense skirt rather than long threads: this animal is a bell first.
  mb.material(JELLY_PART.bloomSkirt);
  const strands = lobes * 3;
  const segs = 10;
  for (let s = 0; s < strands; s++) {
    const a = (s / strands) * Math.PI * 2;
    const rr = R * rng.range(0.72, 0.92);
    const cx = Math.cos(a) * rr, cz = Math.sin(a) * rr;
    const len = rng.range(0.9, 1.9);
    const strandPhase = rng.next();
    const bStart = mb.vertexCount;
    for (let i = 0; i <= segs; i++) {
      const v = i / segs;
      const w = 0.075 * (1 - v * 0.85);
      const x = cx * (1 + v * 0.16) + Math.sin(v * 5 + s) * 0.06 * v;
      const z = cz * (1 + v * 0.16);
      const y = -v * len;
      mb.vertex(x - w, y, z, 0, 0, 1, strandPhase, v);
      mb.vertex(x + w, y, z, 0, 0, 1, strandPhase, v);
    }
    for (let i = 0; i < segs; i++) {
      const q = bStart + i * 2;
      mb.quad(q, q + 1, q + 3, q + 2);
    }
  }

  return mb.build();
}

export const BLOOM_SPECIES = 3;

// ---------------------------------------------------------------------------
// hostiles, structures and props
//
// Everything below is built facing +X; the game yaws 180° for the other facing,
// so nothing here may lean on the mesh being seen from one side only.
// ---------------------------------------------------------------------------

/**
 * Swept trapezoid fin in the XY plane, double sided like `plate`. The root chord
 * runs along X at radius `y0`, the tip chord sits at `y1` shifted by `sweep` and
 * shortened by `taper`. Rolling a few of these around X is the cheapest way to
 * make a silhouette read as built rather than grown.
 */
function fin(
  mb: MeshBuilder,
  x0: number, x1: number, y0: number, y1: number,
  sweep: number, taper: number,
) {
  const mid = (x0 + x1) * 0.5;
  const c = (x1 - x0) * 0.5 * taper;
  const t0 = mid + sweep - c, t1 = mid + sweep + c;
  const b = mb.vertexCount;
  mb.vertex(x0, y0, 0, 0, 0, 1, 0, 0);
  mb.vertex(x1, y0, 0, 0, 0, 1, 1, 0);
  mb.vertex(t1, y1, 0, 0, 0, 1, 1, 1);
  mb.vertex(t0, y1, 0, 0, 0, 1, 0, 1);
  mb.quad(b, b + 1, b + 2, b + 3);
  const d = mb.vertexCount;
  mb.vertex(x0, y0, 0, 0, 0, -1, 0, 0);
  mb.vertex(t0, y1, 0, 0, 0, -1, 0, 1);
  mb.vertex(t1, y1, 0, 0, 0, -1, 1, 1);
  mb.vertex(x1, y0, 0, 0, 0, -1, 1, 0);
  mb.quad(d, d + 1, d + 2, d + 3);
}

/**
 * Curved slab: a rectangular section (inner radius `r0`, outer `r1`, half-depth
 * `hz` in Z) swept through an arc in the XY plane. Armour plate and masonry arch
 * are the same primitive with different numbers.
 */
function arcSlab(
  mb: MeshBuilder,
  r0: number, r1: number, hz: number,
  a0: number, a1: number, bands = 14,
) {
  const sect: [number, number][] = [[r1, -hz], [r1, hz], [r0, hz], [r0, -hz]];
  for (let e = 0; e < 4; e++) {
    const [ra, za] = sect[e];
    const [rb, zb] = sect[(e + 1) % 4];
    const dr = rb - ra, dz = zb - za;
    const l = Math.hypot(dr, dz) || 1;
    // Outward normal of this edge, taken in the (radius, z) plane and then
    // swung around with the sweep.
    const nr = dz / l, nz = -dr / l;
    const b = mb.vertexCount;
    for (let i = 0; i <= bands; i++) {
      const a = a0 + (a1 - a0) * (i / bands);
      const ca = Math.cos(a), sa = Math.sin(a);
      mb.vertex(ca * ra, sa * ra, za, ca * nr, sa * nr, nz, i / bands, 0);
      mb.vertex(ca * rb, sa * rb, zb, ca * nr, sa * nr, nz, i / bands, 1);
    }
    for (let i = 0; i < bands; i++) {
      const q = b + i * 2;
      mb.quad(q, q + 2, q + 3, q + 1);
    }
  }
  // Cut ends, so a broken arch shows its masonry section.
  for (const [a, s] of [[a0, -1], [a1, 1]] as const) {
    const ca = Math.cos(a), sa = Math.sin(a);
    const b = mb.vertexCount;
    for (const [r, z] of sect) mb.vertex(ca * r, sa * r, z, -sa * s, ca * s, 0, 0, 0);
    mb.quad(b, b + 1, b + 2, b + 3);
  }
}

/** Torus lying in the XY plane, so it reads as a full circle from the side. */
function torusZ(mb: MeshBuilder, R: number, r: number, major = 18, minor = 6) {
  const base = mb.vertexCount;
  const stride = minor + 1;
  for (let i = 0; i <= major; i++) {
    const a = (i / major) * Math.PI * 2;
    const ca = Math.cos(a), sa = Math.sin(a);
    for (let j = 0; j <= minor; j++) {
      const b = (j / minor) * Math.PI * 2;
      const cb = Math.cos(b), sb = Math.sin(b);
      mb.vertex(
        (R + r * cb) * ca, (R + r * cb) * sa, r * sb,
        ca * cb, sa * cb, sb, i / major, j / minor,
      );
    }
  }
  for (let i = 0; i < major; i++) {
    for (let j = 0; j < minor; j++) {
      const a = base + i * stride + j;
      mb.quad(a, a + stride, a + stride + 1, a + 1);
    }
  }
}

/** Homing mine: the moored mine's angrier cousin — same horns, but it steers. */
export function buildSeeker(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  // Same reasoning as `buildMine`, one step lower: seekers have a capacity of
  // 180, nearly twice the mines, so 22 x 14 keeps the worst case near 100k.
  // 16 degree facets, still inside metal's lobe.
  sphere(mb, 1.3, 22, 14);

  // Fewer horns than buildMine so the fins still read in silhouette.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const el = i % 2 === 0 ? 0.85 : 0.2;
    const cx = Math.cos(a) * Math.cos(el) * 1.24;
    const cy = Math.sin(el) * 1.24;
    const cz = Math.sin(a) * Math.cos(el) * 1.24;
    mb.transform(cx, cy, cz, 0, 0, -Math.atan2(cx, cy), 1, 1, 1);
    cylinderY(mb, 0.1, 0.06, 0.66, 6);
    mb.popTransform();
  }

  // Three swept fins, rolled 120° apart around the line of travel.
  for (let i = 0; i < 3; i++) {
    mb.transform(0, 0, 0, (i / 3) * Math.PI * 2, 0, 0, 1, 1, 1);
    fin(mb, -1.15, 0.15, 1.02, 2.0, -0.42, 0.5);
    mb.popTransform();
  }

  // Ducted thruster: shroud, grille bars, nozzle.
  mb.transform(-1.62, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.5, 0.6, 0.6, 12, false);
  mb.popTransform();
  for (let i = 0; i < 3; i++) {
    mb.transform(-1.45, 0, 0, (i / 3) * Math.PI * 2 + 0.5, 0, 0, 1, 1, 1);
    box(mb, 0.05, 0.55, 0.04);
    mb.popTransform();
  }
  mb.material(MAT.glow);
  mb.transform(-1.66, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.34, 0.26, 0.2, 10);
  mb.popTransform();
  // Seeker head: the thing that watches you.
  mb.transform(1.3, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.26, 0.19, 0.2, 10);
  mb.popTransform();
  return mb.build();
}

/**
 * Anchored gun emplacement. The flat back face at -X mounts flush to rock; the
 * game spins the whole mesh to aim, so the barrels are built along +X.
 */
export function buildTurret(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  // Armoured dome. The first sliver of the profile is the flat mounting plate.
  revolveX(mb, (t) => {
    if (t < 0.08) return [-0.5, 2.2 * (t / 0.08)];
    const u = (t - 0.08) / 0.92;
    const x = -0.5 + u * 2.1;
    const r = u < 0.3 ? 2.2 : 2.2 * Math.sqrt(Math.max(0, 1 - Math.pow((u - 0.3) / 0.72, 2)));
    return [x, Math.max(0.08, r)];
  }, 12, 16);

  // Mounting flange and an armour band, the same rib trick buildSub uses.
  mb.transform(-0.42, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 2.38, 2.38, 0.18, 16, false);
  mb.popTransform();
  mb.transform(0.3, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 2.22, 2.22, 0.16, 16, false);
  mb.popTransform();

  // Mantlet the barrels pivot through.
  mb.transform(1.55, 0.1, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 0.45, 0.62, 0.95);
  mb.popTransform();

  // Twin barrels out to x = 3.4.
  for (const z of [-0.5, 0.5]) {
    mb.transform(2.5, 0.1, z, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 0.26, 0.2, 1.9, 10);
    mb.popTransform();
    mb.transform(3.24, 0.1, z, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 0.3, 0.3, 0.24, 10, false);
    mb.popTransform();
  }

  // Sensor blister, off-centre so the head never looks symmetrical.
  mb.transform(1.25, 1.3, 0.35, 0, 0, 0, 0.42, 0.34, 0.42);
  sphere(mb, 1, 10, 6);
  mb.popTransform();
  mb.material(MAT.glow);
  mb.transform(1.6, 1.42, 0.35, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.17, 0.13, 0.12, 10);
  mb.popTransform();
  return mb.build();
}

/** Small predatory fish. Flattened hard in Z so a shoal reads as fish. */
export function buildSwarmer(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.organic);
  mb.transform(0, 0, 0, 0, 0, 0, 1, 1, 0.35);
  revolveX(mb, (t) => {
    const x = -0.95 + t * 2.0;
    const r = 0.42 * Math.sin(Math.pow(1 - t, 0.8) * Math.PI) + 0.03;
    return [x, r];
  }, 14, 12);
  mb.popTransform();

  // Gill line, one rib's worth of detail.
  mb.transform(0.34, 0, 0, 0, 0, Math.PI / 2, 1, 0.35, 1);
  cylinderY(mb, 0.4, 0.4, 0.05, 12, false);
  mb.popTransform();

  // Forked caudal fin: two lobes off the peduncle.
  for (const roll of [0, Math.PI]) {
    mb.transform(0, 0, 0, roll, 0, 0, 1, 1, 1);
    fin(mb, -1.02, -0.72, 0.03, 0.52, -0.28, 0.9);
    mb.popTransform();
  }
  // Pectorals, swept back and lying almost flat.
  for (const z of [-0.13, 0.13]) {
    mb.transform(0.28, -0.16, z, Math.PI / 2, 0, 0.35, 1, 1, 1);
    plate(mb, 0.24, 0.12);
    mb.popTransform();
  }
  // Jaw line — a wedge under the snout, hinged open a fraction.
  mb.transform(0.72, -0.11, 0, 0, 0, -0.16, 1, 1, 1);
  box(mb, 0.3, 0.05, 0.14);
  mb.popTransform();
  mb.material(MAT.glow);
  for (const z of [-0.11, 0.11]) {
    mb.transform(0.72, 0.11, z, 0, 0, 0, 0.07, 0.07, 0.07);
    sphere(mb, 1, 6, 4);
    mb.popTransform();
  }
  return mb.build();
}

/**
 * Anglerfish. The mouth is a real hole: the body starts at the jaw rim and a
 * separate flared funnel runs back inside the head, so the gape is a cavity you
 * can see down rather than a painted-on line. Teeth ring the rim, alternating
 * long and short. The lure sphere sits at (2.6, 3.2, 0) for the game's light.
 */
export function buildLurker(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.organic);

  // Skull: still fat at the front, so the head stays a head all the way to the
  // jaw instead of tapering into the mouth like a horn.
  mb.transform(0, 0, 0, 0, 0, 0, 1, 1, 0.86);
  revolveX(mb, (t) => {
    const x = 1.5 - t * 4.5;
    const r = t < 0.28
      ? 1.55 + Math.sin((t / 0.28) * Math.PI * 0.5) * 0.62
      : 2.17 * Math.pow(1 - (t - 0.28) / 0.72, 1.4) + 0.06;
    return [x, Math.max(0.03, r)];
  }, 18, 16);
  mb.popTransform();

  // Gape: a funnel flaring out of the front of the skull, slung low so the jaw
  // juts under the face and the forehead is left standing above it.
  mb.transform(0, -0.35, 0, 0, 0, 0, 1, 1.15, 0.95);
  revolveX(mb, (t) => [1.15 + t * 1.25, 0.45 + Math.pow(t, 0.8) * 1.05], 10, 16);
  mb.popTransform();

  // Brow: a flared band around the front of the skull, which is also what caps
  // off the open ring the body revolve leaves there.
  mb.transform(1.42, 0, 0, 0, 0, Math.PI / 2, 1, 1, 0.86);
  cylinderY(mb, 1.62, 1.5, 0.3, 16, false);
  mb.popTransform();

  // Teeth around the rim, pointing back down the throat. Long and short
  // alternate, which is what makes the jaw line read from across a room.
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    const ry = Math.cos(a) * 1.39 * 1.15, rz = Math.sin(a) * 1.39 * 0.95;
    const len = i % 2 === 0 ? 0.95 : 0.6;
    // Cones are built centred, so pull each one half its length inboard to bury
    // the root in the lip instead of spiking out through it.
    const k = 1 - len * 0.5 / Math.hypot(ry, rz);
    mb.transform(2.25, -0.35 + ry * k, rz * k, Math.atan2(-rz, -ry), 0, 0, 1, 1, 1);
    cylinderY(mb, 0.13, 0, len, 5, false);
    mb.popTransform();
  }

  // Illicium: a stalk arcing up and forward over the head, on a quadratic so
  // the lure ends up out in front of the mouth where the light does most good.
  const A: [number, number] = [0.3, 2.1];
  const B: [number, number] = [0.8, 4.0];
  const C: [number, number] = [2.6, 3.2];
  const at = (s: number): [number, number] => [
    (1 - s) * (1 - s) * A[0] + 2 * (1 - s) * s * B[0] + s * s * C[0],
    (1 - s) * (1 - s) * A[1] + 2 * (1 - s) * s * B[1] + s * s * C[1],
  ];
  for (let i = 0; i < 8; i++) {
    const [x0, y0] = at(i / 8);
    const [x1, y1] = at((i + 1) / 8);
    const dx = x1 - x0, dy = y1 - y0;
    mb.transform(
      (x0 + x1) * 0.5, (y0 + y1) * 0.5, 0,
      0, 0, -Math.atan2(dx, dy), 1, 1, 1,
    );
    cylinderY(mb, 0.14 - i * 0.011, 0.13 - i * 0.011, Math.hypot(dx, dy) * 1.05, 5, false);
    mb.popTransform();
  }

  // Dermal spines along the back.
  for (const [x, y] of [[-0.3, 1.6], [-1.1, 1.0], [-1.9, 0.5], [-2.5, 0.2]] as const) {
    mb.transform(x, y, 0, 0, 0, -0.5, 1, 1, 1);
    cylinderY(mb, 0.13, 0, 0.5, 5, false);
    mb.popTransform();
  }
  // Pectorals and a tail fan, overlapped well onto the whip tail so it never
  // reads as a flag flying loose behind the animal.
  for (const z of [-1.15, 1.15]) {
    mb.transform(-0.5, -0.7, z, Math.PI / 2, 0, 0.25, 1, 1, 1);
    plate(mb, 0.42, 0.26);
    mb.popTransform();
  }
  mb.transform(-2.6, 0, 0, 0, 0, 0, 1, 1, 1);
  plate(mb, 0.5, 0.72);
  mb.popTransform();

  // Eyes, small and high — and the lure itself.
  mb.material(MAT.glow);
  for (const z of [-0.5, 0.5]) {
    mb.transform(1.0, 1.75, z, 0, 0, 0, 0.18, 0.18, 0.18);
    sphere(mb, 1, 8, 5);
    mb.popTransform();
  }
  mb.transform(2.6, 3.2, 0, 0, 0, 0, 1, 1, 1);
  sphere(mb, 0.3, 12, 8);
  mb.popTransform();
  return mb.build();
}

/**
 * Heavy combat drone. The armour plate stands well clear of the core on two
 * struts: at a glance the player should be able to tell which side is shielded
 * and shoot the other one.
 */
export function buildSentinel(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.paint);

  // Hexagonal core, flat facetted so it never reads as another capsule.
  const HEX: [number, number][] = [[-1.75, 0.5], [-1.15, 1.2], [0.9, 1.3], [1.7, 0.66]];
  const b0 = mb.vertexCount;
  for (const [x, r] of HEX) {
    for (let i = 0; i < 6; i++) {
      const a0 = (i / 6) * Math.PI * 2, a1 = ((i + 1) / 6) * Math.PI * 2;
      const am = (a0 + a1) * 0.5;
      const ny = Math.cos(am), nz = Math.sin(am);
      mb.vertex(x, Math.cos(a0) * r, Math.sin(a0) * r, 0, ny, nz, 0, 0);
      mb.vertex(x, Math.cos(a1) * r, Math.sin(a1) * r, 0, ny, nz, 1, 0);
    }
  }
  for (let s = 0; s < HEX.length - 1; s++) {
    for (let i = 0; i < 6; i++) {
      const q = b0 + s * 12 + i * 2;
      mb.quad(q, q + 12, q + 13, q + 1);
    }
  }
  for (const s of [0, HEX.length - 1]) {
    const [x, r] = HEX[s];
    const nx = s === 0 ? -1 : 1;
    const c = mb.vertex(x, 0, 0, nx, 0, 0, 0.5, 0.5);
    const f = mb.vertexCount;
    for (let i = 0; i <= 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      mb.vertex(x, Math.cos(a) * r, Math.sin(a) * r, nx, 0, 0, 0.5, 0.5);
    }
    for (let i = 0; i < 6; i++) mb.tri(c, f + i, f + i + 1);
  }

  // Engine nacelles either side.
  mb.material(MAT.metal);
  for (const z of [-1.5, 1.5]) {
    mb.transform(-0.5, -0.15, z, 0, 0, 0, 1, 1, 1);
    revolveX(mb, (t) => {
      const x = -1.15 + t * 2.3;
      const r = t < 0.7 ? 0.42 : 0.42 * (1 - (t - 0.7) / 0.3 * 0.5);
      return [x, r];
    }, 8, 10);
    mb.popTransform();
    mb.transform(-0.42, 0.55, z, 0, 0, 0, 1, 1, 1);
    box(mb, 0.18, 0.62, 0.14);
    mb.popTransform();
    mb.material(MAT.glow);
    mb.transform(-1.72, -0.15, z, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 0.26, 0.18, 0.14, 10);
    mb.popTransform();
    mb.material(MAT.metal);
  }

  // Riot shield, ±70° at r ≈ 2.6, held off the hull by two struts.
  for (const y of [-0.95, 0.95]) {
    mb.transform(1.95, y, 0, 0, 0, 0, 1, 1, 1);
    box(mb, 0.62, 0.16, 0.22);
    mb.popTransform();
  }
  arcSlab(mb, 2.44, 2.7, 1.2, -1.22, 1.22, 14);

  // Eye, mounted high enough to see over the plate.
  mb.material(MAT.glow);
  mb.transform(0.85, 1.25, 0, 0, 0, 0, 0.3, 0.24, 0.3);
  sphere(mb, 1, 10, 6);
  mb.popTransform();
  return mb.build();
}

/** Hostile attack boat: harder-edged and half again as long as the player's. */
export function buildHunter(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.paint);

  // Pressure hull: tail cone, parallel midbody, a sharp ogive bow.
  revolveX(mb, (t) => {
    const x = -5.5 + t * 11;
    let r: number;
    if (t < 0.2) r = 0.33 + (t / 0.2) * 0.72;
    else if (t < 0.72) r = 1.05;
    else r = 1.05 * Math.pow(Math.max(0, 1 - (t - 0.72) / 0.28), 0.55);
    return [x, Math.max(0.03, r)];
  }, 20, 12);

  // Chine strakes: the hard edge that separates this boat from the player's.
  mb.material(MAT.metal);
  for (const z of [-0.92, 0.92]) {
    mb.transform(1.4, -0.1, z, 0, 0, 0, 1, 1, 1);
    box(mb, 3.1, 0.07, 0.3);
    mb.popTransform();
    mb.transform(4.5, -0.28, z * 0.55, 0, -Math.sign(z) * 0.34, 0, 1, 1, 1);
    box(mb, 0.95, 0.06, 0.26);
    mb.popTransform();
  }

  // Low angular sail, raked forward, with a stub mast.
  mb.material(MAT.paint);
  mb.transform(0.2, 1.45, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 1.35, 0.5, 0.42);
  mb.popTransform();
  mb.transform(1.35, 1.28, 0, 0, 0, -0.42, 1, 1, 1);
  box(mb, 0.55, 0.34, 0.4);
  mb.popTransform();
  mb.material(MAT.metal);
  mb.transform(-0.3, 2.35, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.08, 0.05, 1.1, 6);
  mb.popTransform();
  // Sail planes.
  mb.transform(0.2, 1.5, 0, Math.PI / 2, 0, 0, 1, 1, 1);
  box(mb, 0.4, 0.05, 1.2);
  mb.popTransform();

  // Torpedo tube mouths, stacked so both stay visible in profile.
  for (const y of [-0.34, 0.34]) {
    mb.transform(4.45, y, 0, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 0.25, 0.25, 0.95, 10, false);
    mb.popTransform();
    mb.transform(4.9, y, 0, 0, 0, Math.PI / 2, 1, 1, 1);
    cylinderY(mb, 0.29, 0.29, 0.12, 10, false);
    mb.popTransform();
  }

  // X-form stern planes.
  for (let i = 0; i < 4; i++) {
    mb.transform(0, 0, 0, Math.PI / 4 + (i / 4) * Math.PI * 2, 0, 0, 1, 1, 1);
    fin(mb, -5.0, -3.6, 0.6, 2.2, -0.55, 0.45);
    mb.popTransform();
  }

  // Shrouded propulsor.
  mb.transform(-5.35, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.62, 0.56, 0.85, 12, false);
  mb.popTransform();
  mb.material(MAT.glow);
  mb.transform(-5.5, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 0.4, 0.3, 0.2, 10);
  mb.popTransform();
  // Bridge glass and a bow sensor, the two lit points on an otherwise dark boat.
  mb.transform(1.55, 1.34, 0, 0, 0, -0.42, 1, 1, 1);
  box(mb, 0.3, 0.12, 0.36);
  mb.popTransform();
  mb.transform(3.9, 0.5, 0, 0, 0, 0, 0.18, 0.14, 0.18);
  sphere(mb, 1, 8, 5);
  mb.popTransform();
  return mb.build();
}

/**
 * One dreadnought weapon pod: base, drum, twin barrels, and a lit sensor so the
 * player has something to aim at. `dir` is +1 for the dorsal cluster and -1 for
 * the ventral one — the same hardware bolted to either side of the hull.
 */
function weaponPod(mb: MeshBuilder, x: number, y: number, dir: number) {
  mb.material(MAT.metal);
  mb.transform(x, y, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 2.4, 0.7, 2.0);
  mb.popTransform();
  mb.transform(x, y + dir * 1.2, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 1.5, 1.25, 1.4, 12);
  mb.popTransform();
  mb.transform(x + 1.0, y + dir * 1.8, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 0.7, 0.6, 1.5);
  mb.popTransform();
  // Barrels canted forward and away from the hull.
  const phi = dir * 0.42;
  for (const z of [-0.78, 0.78]) {
    mb.transform(
      x + 1.0 + Math.cos(phi) * 1.45, y + dir * 1.8 + Math.sin(phi) * 1.45, z,
      0, 0, phi - Math.PI / 2, 1, 1, 1,
    );
    cylinderY(mb, 0.3, 0.24, 2.9, 10);
    mb.popTransform();
  }
  mb.material(MAT.glow);
  mb.transform(x - 1.5, y + dir * 1.3, 0, 0, 0, 0, 0.42, 0.42, 0.42);
  sphere(mb, 1, 10, 6);
  mb.popTransform();
}

/**
 * Capital-ship boss. Slab-sided: the cross-section is a superellipse rather than
 * a circle, which is what keeps 46 m of hull from reading as a giant torpedo.
 */
export function buildDreadnought(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.paint);

  // Stations: x, half-height, half-width.
  const HULL: [number, number, number][] = [
    [-24.0, 0.30, 0.25],
    [-22.0, 1.60, 1.20],
    [-18.0, 3.00, 2.30],
    [-12.0, 3.80, 3.00],
    [-4.0, 4.00, 3.20],
    [6.0, 4.00, 3.20],
    [12.0, 3.70, 2.90],
    [17.0, 3.10, 2.40],
    [20.5, 2.20, 1.70],
    [22.5, 0.30, 0.25],
  ];
  const station = (t: number): [number, number, number] => {
    const s = t * (HULL.length - 1);
    const i = Math.min(HULL.length - 2, Math.floor(s));
    const f = s - i;
    const a = HULL[i], b = HULL[i + 1];
    return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
  };
  const pos = (t: number, a: number): [number, number, number] => {
    const [x, hy, hz] = station(t);
    const c = Math.cos(a), s = Math.sin(a);
    const k = 0.55; // 1 would be an ellipse; lower squares off the corners
    return [
      x,
      Math.sign(c) * Math.pow(Math.abs(c), k) * hy,
      Math.sign(s) * Math.pow(Math.abs(s), k) * hz,
    ];
  };
  const rings = 18, seg = 16;
  const base = mb.vertexCount;
  for (let r = 0; r <= rings; r++) {
    const t = r / rings;
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const [x, y, z] = pos(t, a);
      const e = 0.01;
      // Central difference, so the bow and stern rings get a real tangent
      // instead of a degenerate one.
      const [xt, yt, zt] = pos(Math.min(1, t + e), a);
      const [xp, yp, zp] = pos(Math.max(0, t - e), a);
      const [xa, ya, za] = pos(t, a + e);
      const ux = xt - xp, uy = yt - yp, uz = zt - zp;
      const vx = xa - x, vy = ya - y, vz = za - z;
      // v × u, which is the outward side for a hull swept bow-to-stern — the
      // same sense revolveX gives every other boat in here.
      const nx = vy * uz - vz * uy;
      const ny = vz * ux - vx * uz;
      const nz = vx * uy - vy * ux;
      const l = Math.hypot(nx, ny, nz) || 1;
      mb.vertex(x, y, z, nx / l, ny / l, nz / l, i / seg, t);
    }
  }
  const stride = seg + 1;
  for (let r = 0; r < rings; r++) {
    for (let i = 0; i < seg; i++) {
      const a = base + r * stride + i;
      mb.quad(a, a + stride, a + stride + 1, a + 1);
    }
  }

  // Deck edge strakes and a keel box: horizontal lines are what sell scale.
  mb.material(MAT.metal);
  for (const z of [-3.1, 3.1]) {
    mb.transform(1, 1.9, z, 0, 0, 0, 1, 1, 1);
    box(mb, 15, 0.22, 0.16);
    mb.popTransform();
    mb.transform(1, -2.2, z, 0, 0, 0, 1, 1, 1);
    box(mb, 15, 0.18, 0.14);
    mb.popTransform();
  }
  mb.transform(-2, -4.05, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 14, 0.3, 1.1);
  mb.popTransform();
  // Raised citadel deck: the flat run of armour the pods and tower stand on,
  // and the single detail that stops 46 m of hull reading as a torpedo.
  mb.transform(0, 4.15, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 14, 0.55, 2.5);
  mb.popTransform();
  // Deck hatches along the spine.
  for (let i = 0; i < 6; i++) {
    mb.transform(-13 + i * 5.2, 4.9, 0, 0, 0, 0, 1, 1, 1);
    box(mb, 1.0, 0.22, 0.9);
    mb.popTransform();
  }
  // Flank sponsons, breaking the hull line into armoured blocks.
  for (const z of [-3.0, 3.0]) {
    for (const x of [-6.5, 4.5]) {
      mb.transform(x, 0.2, z, 0, 0, 0, 1, 1, 1);
      box(mb, 2.6, 1.5, 0.5);
      mb.popTransform();
    }
  }

  // Command tower: long and slab-sided rather than tall, so it still reads as
  // part of the fortress at 46 m away.
  mb.material(MAT.paint);
  mb.transform(-3.0, 5.8, 0, 0, 0, 0, 1, 1, 1);
  box(mb, 4.6, 1.6, 1.7);
  mb.popTransform();
  mb.transform(-2.4, 7.6, 0, 0, 0, -0.1, 1, 1, 1);
  box(mb, 2.8, 1.0, 1.2);
  mb.popTransform();
  mb.material(MAT.metal);
  mb.transform(1.9, 6.2, 0, 0, 0, 0.5, 1, 1, 1);
  box(mb, 1.3, 0.5, 1.3);
  mb.popTransform();
  mb.material(MAT.metal);
  mb.transform(-3.6, 8.9, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.16, 0.1, 1.4, 6);
  mb.popTransform();
  mb.material(MAT.glow);
  mb.transform(-0.45, 7.7, 0, 0, 0, -0.1, 1, 1, 1);
  box(mb, 0.5, 0.2, 1.23);
  mb.popTransform();

  // Bow torpedo doors, set on the flanks so they read from the side.
  mb.material(MAT.metal);
  for (const z of [-2.35, 2.35]) {
    for (const y of [-0.6, 1.1]) {
      mb.transform(17.5, y, z, Math.PI / 2, 0, 0, 1, 1, 1);
      cylinderY(mb, 0.62, 0.62, 0.22, 10, false);
      mb.popTransform();
    }
  }

  // The two turret clusters — one forward on the spine, one aft underneath.
  weaponPod(mb, 10.5, 4.0, 1);
  weaponPod(mb, -9.0, -3.2, -1);

  // Stern planes and a screw big enough to be a landmark.
  mb.material(MAT.metal);
  for (let i = 0; i < 4; i++) {
    mb.transform(0, 0, 0, Math.PI / 4 + (i / 4) * Math.PI * 2, 0, 0, 1, 1, 1);
    fin(mb, -21.5, -17.5, 2.6, 5.8, -1.8, 0.45);
    mb.popTransform();
  }
  mb.transform(-24.6, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 2.6, 2.35, 2.6, 16, false);
  mb.popTransform();
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    mb.transform(-24.6, Math.cos(a) * 1.3, Math.sin(a) * 1.3, a, 0.3, 0, 1, 1, 1);
    plate(mb, 1.2, 1.2);
    mb.popTransform();
  }
  mb.material(MAT.glow);
  mb.transform(-25.4, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  cylinderY(mb, 1.1, 0.85, 0.4, 12);
  mb.popTransform();
  return mb.build();
}

/**
 * Repair-and-save pylon. Everything about it is straight lines and right angles:
 * in a dark ocean of lumpy rock that alone says "someone built this".
 */
export function buildStation(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);

  // Weighted base.
  mb.transform(0, 0.3, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 3.0, 2.5, 0.6, 12);
  mb.popTransform();
  mb.transform(0, 0.66, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 3.1, 3.1, 0.16, 12, false);
  mb.popTransform();
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.4;
    mb.transform(Math.cos(a) * 2.3, 0.9, Math.sin(a) * 2.3, 0, -a, 0, 1, 1, 1);
    box(mb, 0.5, 0.4, 0.7);
    mb.popTransform();
  }

  // Lattice mast: four legs leaning in, braced with a zig-zag on both faces.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      mb.transform(sx * 0.58, 3.8, sz * 0.55, 0, 0, -sx * 0.05, 1, 1, 1);
      box(mb, 0.09, 3.3, 0.09);
      mb.popTransform();
    }
  }
  for (const sz of [-1, 1]) {
    for (let i = 0; i < 6; i++) {
      const y = 0.9 + i * 1.1;
      const dir = i % 2 === 0 ? 1 : -1;
      mb.transform(0, y + 0.55, sz * 0.55, 0, 0, dir * 0.79, 1, 1, 1);
      box(mb, 0.82, 0.06, 0.06);
      mb.popTransform();
    }
  }
  for (const y of [1.6, 3.4, 5.2, 6.6]) {
    mb.transform(0, y, 0, 0, 0, 0, 1, 1, 1);
    box(mb, 0.62, 0.06, 0.6);
    mb.popTransform();
  }

  // Mooring arms, one either side, hooks turned up.
  for (const sx of [-1, 1]) {
    mb.transform(sx * 2.3, 5.5, 0, 0, 0, 0, 1, 1, 1);
    box(mb, 1.6, 0.14, 0.14);
    mb.popTransform();
    mb.transform(sx * 3.75, 5.75, 0, 0, 0, sx * 0.6, 1, 1, 1);
    box(mb, 0.45, 0.13, 0.13);
    mb.popTransform();
  }

  // Halo: the ring you dock inside, and the only thing visible from far off.
  mb.material(MAT.glow);
  mb.transform(0, 7.4, 0, 0, 0, 0, 1, 1, 1);
  torusZ(mb, 1.6, 0.17, 20, 6);
  mb.popTransform();
  for (const sx of [-1, 1]) {
    mb.transform(sx * 4.05, 5.95, 0, 0, 0, 0, 0.2, 0.2, 0.2);
    sphere(mb, 1, 8, 5);
    mb.popTransform();
  }
  mb.transform(0, 8.85, 0, 0, 0, 0, 0.26, 0.34, 0.26);
  sphere(mb, 1, 8, 5);
  mb.popTransform();
  // Marker strips up the mast.
  for (const y of [2.2, 4.0, 5.8]) {
    mb.transform(0, y, 0.6, 0, 0, 0, 1, 1, 1);
    box(mb, 0.34, 0.07, 0.04);
    mb.popTransform();
  }
  return mb.build();
}

/** Powerup canister: ribbed barrel with a collar broad enough to catch light. */
export function buildCanister(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.metal);
  mb.transform(0, 0, 0, 0, 0, Math.PI / 2, 1, 1, 1);
  capsuleX(mb, 0.42, 0.36, 12, 12);
  mb.popTransform();

  // Ribbed midsection.
  for (let i = 0; i < 5; i++) {
    mb.transform(0, -0.32 + i * 0.16, 0, 0, 0, 0, 1, 1, 1);
    cylinderY(mb, 0.39, 0.39, 0.055, 12, false);
    mb.popTransform();
  }
  // End caps.
  for (const y of [-0.72, 0.72]) {
    mb.transform(0, y, 0, 0, 0, 0, 1, 1, 1);
    cylinderY(mb, 0.3, 0.25, 0.14, 12);
    mb.popTransform();
  }
  // Collar — deliberately oversized; it is what glints as the canister turns.
  mb.transform(0, 0, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.64, 0.64, 0.11, 14);
  mb.popTransform();
  mb.material(MAT.glow);
  mb.transform(0, 0.16, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.4, 0.4, 0.08, 12, false);
  mb.popTransform();
  mb.transform(0, 0.79, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, 0.2, 0.15, 0.14, 10);
  mb.popTransform();
  return mb.build();
}

/** Black smoker: a gnarled chimney, jittered on the same seam-safe table as buildRock. */
export function buildVent(): MeshData {
  const mb = new MeshBuilder();
  const rng = new Rng(60313);
  mb.material(MAT.organic);

  const seg = 13, rings = 16, H = 7;
  const jitter: number[] = [];
  for (let r = 0; r <= rings; r++) {
    for (let i = 0; i <= seg; i++) {
      jitter.push(i === seg ? jitter[jitter.length - seg] : rng.range(0.8, 1.2));
    }
  }
  // Wide crusty foot, a couple of swellings, a narrow throat.
  const lean = (t: number) => Math.sin(t * 2.3) * 0.5 + t * 0.3;
  const rad = (t: number) => 1.75 * Math.pow(1 - t, 1.7) + 0.3 + Math.sin(t * 6.5) * 0.13;
  const nl = Math.hypot(1, 0.3);
  const base = mb.vertexCount;
  let k = 0;
  for (let r = 0; r <= rings; r++) {
    const t = r / rings;
    const y = t * H, cx = lean(t), rr = rad(t);
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const nx = Math.cos(a), nz = Math.sin(a);
      const j = jitter[k++];
      mb.vertex(cx + nx * rr * j, y, nz * rr * j, nx / nl, 0.3 / nl, nz / nl, i / seg, t);
    }
  }
  const stride = seg + 1;
  for (let r = 0; r < rings; r++) {
    for (let i = 0; i < seg; i++) {
      const a = base + r * stride + i;
      mb.quad(a, a + stride, a + stride + 1, a + 1);
    }
  }

  // Side spouts, kept in the XY plane so they show in silhouette.
  for (const [t, dir] of [[0.3, 1], [0.52, -1], [0.72, 1]] as const) {
    mb.transform(
      lean(t) + dir * rad(t) * 0.7, t * H, rng.range(-0.3, 0.3),
      0, dir > 0 ? 0 : Math.PI, dir * 0.85, 1, 1, 1,
    );
    revolveX(mb, (u) => [u * 1.5, 0.34 * (1 - u * 0.75) + 0.05], 5, 6);
    mb.popTransform();
  }
  // Sulphide rubble piled around the foot.
  for (let i = 0; i < 4; i++) {
    const a = rng.range(0, Math.PI * 2);
    const rr = rng.range(1.5, 2.4);
    mb.transform(
      Math.cos(a) * rr, rng.range(0.1, 0.6), Math.sin(a) * rr, 0, 0, 0,
      rng.range(0.5, 0.95), rng.range(0.35, 0.7), rng.range(0.5, 0.95),
    );
    sphere(mb, 1, 6, 4);
    mb.popTransform();
  }
  // The throat, hot enough to see.
  mb.material(MAT.glow);
  mb.transform(lean(1), H - 0.1, 0, 0, 0, 0, 1, 1, 1);
  cylinderY(mb, rad(1) * 0.62, rad(1) * 0.5, 0.3, 10);
  mb.popTransform();
  return mb.build();
}

/**
 * A block of sunken city. Three variants — column, arch, wall — all about 10 m
 * across and all sitting on y = 0, so scattered copies at different scales and
 * yaws still line up into something that reads as one ruined street.
 */
export function buildRuin(seed: number): MeshData {
  const mb = new MeshBuilder();
  const rng = new Rng(seed * 2654435761 + 991);
  mb.material(MAT.organic);

  switch (rng.int(0, 2)) {
    case 0: {
      // Broken column: fluted shaft, snapped off at an uneven height.
      const h = rng.range(6.0, 8.5);
      const seg = 24, ringsC = 5;
      const brk: number[] = [];
      for (let i = 0; i <= seg; i++) {
        brk.push(i === seg ? brk[0] : rng.range(-0.7, 0.35));
      }
      const base = mb.vertexCount;
      for (let r = 0; r <= ringsC; r++) {
        const t = r / ringsC;
        for (let i = 0; i <= seg; i++) {
          const a = (i / seg) * Math.PI * 2;
          const nx = Math.cos(a), nz = Math.sin(a);
          const rr = (0.95 - t * 0.12) * (1 + 0.055 * Math.cos(a * 12));
          const y = 1.1 + t * (h - 1.1) + (r === ringsC ? brk[i] : 0);
          mb.vertex(nx * rr, y, nz * rr, nx, 0, nz, i / seg, t);
        }
      }
      const stride = seg + 1;
      for (let r = 0; r < ringsC; r++) {
        for (let i = 0; i < seg; i++) {
          const a = base + r * stride + i;
          mb.quad(a, a + stride, a + stride + 1, a + 1);
        }
      }
      mb.transform(0, 0.3, 0, 0, 0, 0, 1, 1, 1);
      box(mb, 1.6, 0.3, 1.6);
      mb.popTransform();
      mb.transform(0, 0.85, 0, 0, 0, 0, 1, 1, 1);
      cylinderY(mb, 1.3, 1.1, 0.5, 12);
      mb.popTransform();
      // A drum off the top, lying where it fell.
      mb.transform(rng.range(2.6, 4.0), 0.95, rng.range(-1.2, 1.2), 0, rng.range(0, 3), Math.PI / 2, 1, 1, 1);
      cylinderY(mb, 0.95, 0.9, 1.3, 10);
      mb.popTransform();
      break;
    }
    case 1: {
      // Collapsed arch: springs off the right pier, snapped before the crown.
      for (const s of [-1, 1]) {
        mb.transform(s * 3.3, 2.1, 0, 0, 0, 0, 1, 1, 1);
        box(mb, 0.85, 2.1, 0.85);
        mb.popTransform();
        mb.transform(s * 3.3, 4.4, 0, 0, 0, 0, 1, 1, 1);
        box(mb, 1.05, 0.25, 1.0);
        mb.popTransform();
      }
      mb.transform(0, 4.65, 0, 0, 0, 0, 1, 1, 1);
      arcSlab(mb, 2.6, 3.95, 0.7, 0, Math.PI - rng.range(0.35, 0.9), 14);
      mb.popTransform();
      // Voussoirs from the missing half.
      for (let i = 0; i < 3; i++) {
        mb.transform(
          rng.range(-4.5, -1.0), rng.range(0.4, 0.9), rng.range(-1.4, 1.4),
          rng.range(-1, 1), rng.range(-1, 1), rng.range(-1, 1), 1, 1, 1,
        );
        box(mb, 0.75, 0.5, 0.65);
        mb.popTransform();
      }
      break;
    }
    default: {
      // Slab wall: four panels frame the window, so the opening is a real hole.
      mb.transform(-3.1, 2.6, 0, 0, 0, 0, 1, 1, 1);
      box(mb, 1.9, 2.6, 0.35);
      mb.popTransform();
      mb.transform(3.1, 2.6, 0, 0, 0, 0, 1, 1, 1);
      box(mb, 1.9, 2.6, 0.35);
      mb.popTransform();
      mb.transform(0, 0.55, 0, 0, 0, 0, 1, 1, 1);
      box(mb, 1.2, 0.55, 0.35);
      mb.popTransform();
      mb.transform(0, 4.25, 0, 0, 0, 0, 1, 1, 1);
      box(mb, 1.2, 0.95, 0.35);
      mb.popTransform();
      // Broken coping along the top.
      for (let i = 0; i < 5; i++) {
        const hy = rng.range(0.15, 0.75);
        mb.transform(-4 + i * 2, 5.2 + hy, 0, 0, 0, rng.range(-0.06, 0.06), 1, 1, 1);
        box(mb, 0.95, hy, 0.33);
        mb.popTransform();
      }
      // A crack running out of the window corner, and rubble at the foot.
      mb.transform(1.7, 3.6, 0, 0, 0, rng.range(0.6, 1.1), 1, 1, 1);
      box(mb, 1.1, 0.09, 0.38);
      mb.popTransform();
      for (let i = 0; i < 3; i++) {
        mb.transform(
          rng.range(-5, 5), rng.range(0.15, 0.5), rng.range(-1.5, 1.5),
          rng.range(-1, 1), rng.range(-1, 1), rng.range(-1, 1), 1, 1, 1,
        );
        box(mb, rng.range(0.4, 0.9), 0.35, rng.range(0.4, 0.8));
        mb.popTransform();
      }
      break;
    }
  }
  return mb.build();
}

/**
 * Ancient-structure monolith. The cross-section is a chamfered rectangle with a
 * channel sunk into every face, lofted with a taper and closed by a point; every
 * edge is duplicated so the creases stay razor sharp. Nothing natural in this
 * ocean has a straight line on it, which is the whole idea.
 */
export function buildMonolith(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.paint);

  const HW = 1.35, HD = 0.95;   // half width in X, half depth in Z, at the base
  const CH = 0.26;              // corner chamfer
  const G = 0.34, D = 0.16;     // channel half-width and depth
  const H = 11.4, TIP = 2.6;

  // Each face is walked in its own (outward, along) frame; the gaps between the
  // faces close up as the chamfers.
  const faces: [number, number, number, number, number, number][] = [
    [1, 0, 0, 1, HW, HD],
    [0, 1, -1, 0, HD, HW],
    [-1, 0, 0, -1, HW, HD],
    [0, -1, 1, 0, HD, HW],
  ];
  const ring: [number, number][] = [];
  for (const [ox, oz, ax, az, out, span] of faces) {
    const prof: [number, number][] = [
      [out, -span + CH],
      [out, -G],
      [out - D, -G + 0.07],
      [out - D, G - 0.07],
      [out, G],
      [out, span - CH],
    ];
    for (const [o, s] of prof) ring.push([ox * o + ax * s, oz * o + az * s]);
  }

  const rings = 5;
  const scaleAt = (t: number) => 1 - 0.3 * Math.pow(t, 1.25);
  const n = ring.length;
  for (let e = 0; e < n; e++) {
    const [x0, z0] = ring[e];
    const [x1, z1] = ring[(e + 1) % n];
    const dx = x1 - x0, dz = z1 - z0;
    const l = Math.hypot(dx, dz) || 1;
    const nx = dz / l, nz = -dx / l;
    const b = mb.vertexCount;
    for (let r = 0; r <= rings; r++) {
      const t = r / rings;
      const s = scaleAt(t), y = t * H;
      mb.vertex(x0 * s, y, z0 * s, nx, 0, nz, 0, t);
      mb.vertex(x1 * s, y, z1 * s, nx, 0, nz, 1, t);
    }
    for (let r = 0; r < rings; r++) {
      const q = b + r * 2;
      mb.quad(q, q + 2, q + 3, q + 1);
    }
  }
  // Pointed cap.
  const s = scaleAt(1);
  for (let e = 0; e < n; e++) {
    const [x0, z0] = ring[e];
    const [x1, z1] = ring[(e + 1) % n];
    const ax = x0 * s, az = z0 * s, bx = x1 * s, bz = z1 * s;
    // Outward face normal of the triangle running up to the apex, i.e.
    // (apex - a) × (b - a). The cap edge lies flat in the y = H plane.
    const ux = bx - ax, uz = bz - az;
    const vx = -ax, vy = TIP, vz = -az;
    let nx = uz * vy;
    let ny = ux * vz - uz * vx;
    let nz = -ux * vy;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l; ny /= l; nz /= l;
    const b = mb.vertexCount;
    mb.vertex(ax, H, az, nx, ny, nz, 0, 0);
    mb.vertex(0, H + TIP, 0, nx, ny, nz, 0.5, 1);
    mb.vertex(bx, H, bz, nx, ny, nz, 1, 0);
    mb.tri(b, b + 1, b + 2);
  }
  return mb.build();
}

/** Small wreckage: a bent plate, a torn girder or a crumpled canister. */
export function buildDebris(seed: number): MeshData {
  const mb = new MeshBuilder();
  const rng = new Rng(seed * 2654435761 + 5231);
  mb.material(MAT.metal);

  switch (rng.int(0, 2)) {
    case 0: {
      // Hull plate, folded once and torn ragged along both long edges.
      const segs = 5;
      const w = rng.range(0.55, 1.1);
      const bend = rng.range(0.5, 1.3);
      const fold = rng.range(0.25, 0.5);
      const b = mb.vertexCount;
      for (let i = 0; i <= segs; i++) {
        const u = i / segs;
        const x = -1.1 + u * 2.2;
        const y = Math.pow(Math.max(0, u - fold), 2) * bend;
        const slope = 2 * Math.max(0, u - fold) * bend / 2.2;
        const l = Math.hypot(slope, 1);
        mb.vertex(x, y, -w + rng.range(-0.16, 0.16), -slope / l, 1 / l, 0, u, 0);
        mb.vertex(x, y, w + rng.range(-0.16, 0.16), -slope / l, 1 / l, 0, u, 1);
      }
      for (let i = 0; i < segs; i++) {
        const q = b + i * 2;
        mb.quad(q, q + 1, q + 3, q + 2);
      }
      // Stiffeners, still attached.
      for (const x of [-0.5, 0.45]) {
        mb.transform(x, 0.06, 0, 0, 0, 0, 1, 1, 1);
        box(mb, 0.06, 0.12, w * 0.9);
        mb.popTransform();
      }
      break;
    }
    case 1: {
      // Torn girder: two halves of an I-beam, kinked where it gave way.
      const kink = rng.range(-0.4, 0.4);
      for (const s of [-1, 1]) {
        const len = rng.range(0.7, 1.4);
        const rz = s < 0 ? 0 : kink;
        mb.transform(s * len * 0.9, s * len * 0.9 * (s < 0 ? 0 : kink), 0, 0, 0, rz, 1, 1, 1);
        box(mb, len, 0.05, 0.32);
        mb.popTransform();
        for (const dy of [-0.34, 0.34]) {
          mb.transform(
            s * len * 0.9, s * len * 0.9 * (s < 0 ? 0 : kink) + dy, 0,
            0, 0, rz, 1, 1, 1,
          );
          box(mb, len, 0.05, 0.06);
          mb.popTransform();
        }
      }
      break;
    }
    default: {
      // Crumpled canister: a cylinder with the dents baked into the vertices.
      const seg = 9, ringsD = 5, h = rng.range(1.2, 2.2);
      const dent: number[] = [];
      for (let r = 0; r <= ringsD; r++) {
        for (let i = 0; i <= seg; i++) {
          dent.push(i === seg ? dent[dent.length - seg] : rng.range(0.48, 1.2));
        }
      }
      const base = mb.vertexCount;
      let k = 0;
      for (let r = 0; r <= ringsD; r++) {
        const t = r / ringsD;
        for (let i = 0; i <= seg; i++) {
          const a = (i / seg) * Math.PI * 2;
          const nx = Math.cos(a), nz = Math.sin(a);
          const rr = 0.42 * dent[k++];
          mb.vertex(nx * rr, t * h, nz * rr, nx, 0, nz, i / seg, t);
        }
      }
      const stride = seg + 1;
      for (let r = 0; r < ringsD; r++) {
        for (let i = 0; i < seg; i++) {
          const a = base + r * stride + i;
          mb.quad(a, a + stride, a + stride + 1, a + 1);
        }
      }
      for (const y of [0.02, h - 0.02]) {
        mb.transform(0, y, 0, 0, 0, 0, 1, 1, 1);
        cylinderY(mb, 0.46, 0.46, 0.1, 9);
        mb.popTransform();
      }
      break;
    }
  }
  return mb.build();
}

/**
 * Double-sided elliptical fan in the XY plane, centred on (cx, cy) and swept
 * from a0 to a1. A rounded tail, a tall dorsal sail and a caudal paddle are the
 * same shape at three aspect ratios, so every soft fin below comes out of this
 * one call. `scallop` waves the rim in and out a vertex at a time, which is the
 * cheapest way to stop a big fin reading as a piece of card.
 */
function finFan(
  mb: MeshBuilder,
  cx: number, cy: number, rx: number, ry: number,
  a0: number, a1: number, steps = 8, scallop = 0,
) {
  const rim: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const a = a0 + (a1 - a0) * (i / steps);
    const k = 1 + scallop * Math.sin(i * Math.PI * 0.5);
    rim.push([cx + Math.cos(a) * rx * k, cy + Math.sin(a) * ry * k]);
  }
  for (const nz of [1, -1]) {
    const c = mb.vertex(cx, cy, 0, 0, 0, nz, 0.5, 0.5);
    const b = mb.vertexCount;
    for (const [x, y] of rim) {
      mb.vertex(x, y, 0, 0, 0, nz, (x - cx) / rx * 0.5 + 0.5, (y - cy) / ry * 0.5 + 0.5);
    }
    for (let i = 0; i < steps; i++) {
      if (nz > 0) mb.tri(c, b + i, b + i + 1);
      else mb.tri(c, b + i + 1, b + i);
    }
  }
}

/**
 * `revolveX` with a centreline that is allowed to wander in Y, and an elliptical
 * section rather than a round one. `path(t)` returns [x, y, radius]; `flat`
 * squashes the section in Z. This is what an eel needs and a torpedo does not:
 * the bend has to be in the geometry, because a straight tube with a wavy fin
 * bolted on still reads as a pipe.
 */
function sweptTubeXY(
  mb: MeshBuilder,
  path: (t: number) => [number, number, number],
  flat: number,
  steps: number,
  seg: number,
) {
  const base = mb.vertexCount;
  const stride = seg + 1;
  for (let r = 0; r <= steps; r++) {
    const t = r / steps;
    const [x, y, rad] = path(t);
    const e = 1 / (steps * 4);
    const [xa, ya, ra] = path(Math.max(0, t - e));
    const [xb, yb, rb] = path(Math.min(1, t + e));
    const dx = xb - xa, dy = yb - ya, dr = rb - ra;
    const tl = Math.hypot(dx, dy) || 1;
    const tx = dx / tl, ty = dy / tl;   // along the body
    const ux = -ty, uy = tx;            // across it, still in the XY plane
    const sl = Math.hypot(dr, tl) || 1;
    const nAxial = -dr / sl, nRadial = tl / sl;
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * Math.PI * 2;
      const ca = Math.cos(a), sa = Math.sin(a);
      // Outward normal of an ellipse with semi-axes (rad, rad * flat), taken in
      // the section frame and then leaned back by the profile's own slope.
      const ex = ux * ca * flat, ey = uy * ca * flat, ez = sa;
      const el = Math.hypot(ex, ey, ez) || 1;
      mb.vertex(
        x + ux * ca * rad, y + uy * ca * rad, sa * rad * flat,
        tx * nAxial + (ex / el) * nRadial,
        ty * nAxial + (ey / el) * nRadial,
        (ez / el) * nRadial,
        i / seg, t,
      );
    }
  }
  for (let r = 0; r < steps; r++) {
    for (let i = 0; i < seg; i++) {
      const a = base + r * stride + i;
      mb.quad(a, a + stride, a + stride + 1, a + 1);
    }
  }
}

export const FISH_SPECIES = 4;

/**
 * Shoal fish: four silhouettes off one call, so a mixed school reads as a school
 * and not as one shape stamped two hundred times.
 *
 * Everything here is outline. At thirty pixels tall a fish is a blot with a tail
 * on it, so the triangles go into body depth, fin area and tail shape and
 * nowhere else — the four differ in proportion first, ornament last. All face
 * +X, all are squashed to roughly a third of their depth in Z, and all are 2–3 m
 * nose to tail bar the ribbon, which is twice that and a fifth as thick.
 *
 * No colour is baked in: bodies are MAT.organic and eyes and light organs
 * MAT.glow, and the game tints and lights each instance. The eyes are separate
 * spheres standing slightly proud of the skull so a highlight has something to
 * sit on, and species 1's belly organs are separate spheres for the same reason.
 */
export function buildFishSpecies(i: number, lod = 0): MeshData {
  const kind = ((i % FISH_SPECIES) + FISH_SPECIES) % FISH_SPECIES;
  const mb = new MeshBuilder();
  const rng = new Rng(kind * 2654435761 + 733);
  mb.material(MAT.organic);
  // Far build. The silhouette is everything that survives at a few pixels, so
  // the outline pieces — tails, sails, the eel's dorsal ribbon — keep their
  // shape and only lose rings around the body. What goes entirely is the
  // sub-pixel jewellery: eyes, belly organs, gill lines. Those were more than
  // half the triangle count on three of the four species, spent on beads
  // smaller than one pixel in every frame a bait ball has ever appeared in.
  const lo = lod > 0;
  const T = (n: number) => (lo ? Math.max(3, Math.round(n * 0.5)) : n);

  switch (kind) {
    case 0: {
      // Darter — the swarmer drawn out longer and pared thinner, with the
      // shoulder pushed right forward so the whole animal tapers away aft.
      const flat = 0.32;
      const at = (t: number) => -1.2 + t * 2.45;
      const prof = (t: number) => 0.29 * Math.sin(Math.pow(1 - t, 0.62) * Math.PI) + 0.025;
      mb.transform(0, 0, 0, 0, 0, 0, 1, 1, flat);
      revolveX(mb, (t) => [at(t), prof(t)], T(16), T(10));
      mb.popTransform();

      // Gill line, one rib's worth of detail, flattened with the flank so it
      // sits in the hull instead of standing off it as a collar.
      if (!lo) {
        mb.transform(0.6, 0, 0, 0, 0, Math.PI / 2, 1, 1, flat);
        cylinderY(mb, 0.27, 0.27, 0.04, 10, false);
        mb.popTransform();
      }

      // Deeply forked caudal: two long lobes off a thin peduncle. That fork is
      // the one thing on this fish that still says "fast" at a hundred metres.
      const rake = rng.range(-0.38, -0.28);
      for (const roll of [0, Math.PI]) {
        mb.transform(0, 0, 0, roll, 0, 0, 1, 1, 1);
        fin(mb, -1.3, -0.92, 0.02, 0.56, rake, 0.6);
        mb.popTransform();
      }
      // Low dorsal and anal keels — little area, but they keep the profile from
      // reading as a spindle.
      fin(mb, 0.05, 0.5, 0.23, 0.46, -0.12, 0.55);
      mb.transform(0, 0, 0, Math.PI, 0, 0, 1, 1, 1);
      fin(mb, -0.18, 0.18, 0.2, 0.4, -0.1, 0.6);
      mb.popTransform();
      // Pectorals, swept back and lying almost flat against the flank.
      for (const z of [-0.09, 0.09]) {
        mb.transform(0.42, -0.13, z, Math.PI / 2, 0, rng.range(0.3, 0.5), 1, 1, 1);
        plate(mb, 0.22, 0.1);
        mb.popTransform();
      }
      if (!lo) {
        mb.material(MAT.glow);
        for (const z of [-0.085, 0.085]) {
          mb.transform(0.92, 0.09, z, 0, 0, 0, 0.07);
          sphere(mb, 1, 6, 4);
          mb.popTransform();
        }
      }
      break;
    }

    case 1: {
      // Lantern — deep, blunt and all belly, built round a lens far too big for
      // the skull. Deep-sea fish that farm their own light usually are.
      const flat = 0.34;
      const at = (t: number) => -0.98 + t * 1.95;
      const prof = (t: number) =>
        0.58 * Math.pow(Math.sin(Math.pow(1 - t, 0.85) * Math.PI), 0.68) + 0.035;
      mb.transform(0, 0, 0, 0, 0, 0, 1, 1, flat);
      revolveX(mb, (t) => [at(t), prof(t)], T(12), T(10));
      mb.popTransform();

      // Short rounded tail: a paddle, not a fork. Half the difference between
      // this fish and the darter is here.
      finFan(mb, -0.9, 0, 0.45, 0.4, Math.PI * 0.42, Math.PI * 1.58, T(10), 0.05);
      // Soft dorsal and anal humps, both following the curve of the body.
      finFan(mb, -0.05, 0.3, 0.55, 0.5, 0.3, Math.PI - 0.3, T(8), 0.05);
      mb.transform(0, 0, 0, Math.PI, 0, 0, 1, 1, 1);
      finFan(mb, -0.2, 0.28, 0.46, 0.42, 0.35, Math.PI - 0.35, T(8), 0.05);
      mb.popTransform();
      for (const z of [-0.14, 0.14]) {
        mb.transform(0.3, -0.24, z, Math.PI / 2, 0, 0.5, 1, 1, 1);
        plate(mb, 0.2, 0.1);
        mb.popTransform();
      }

      // Belly lights, kept as free-standing spheres rather than sunk into the
      // hull so the emissive pass has whole objects to find. Spacing and size
      // are jittered a little; a perfectly even row reads as a dotted rule.
      // The organs are not what makes a lantern fish glow — bioluminescence
      // is a whole-body term keyed on params2.y — so dropping them at distance
      // costs the light nothing.
      if (!lo) {
        mb.material(MAT.glow);
        for (let k = 0; k < 6; k++) {
          const t = 0.14 + (k / 5) * 0.68;
          mb.transform(
            at(t) + rng.range(-0.03, 0.03), -prof(t) * 0.92, 0,
            0, 0, 0, rng.range(0.05, 0.075),
          );
          sphere(mb, 1, 5, 4);
          mb.popTransform();
        }
        // The eye — the other half of the silhouette, and deliberately proud
        // of the cheek so it catches a rim light from any angle.
        for (const z of [-0.13, 0.13]) {
          mb.transform(0.55, 0.19, z, 0, 0, 0, 0.17, 0.17, 0.15);
          sphere(mb, 1, 6, 4);
          mb.popTransform();
        }
      }
      break;
    }

    case 2: {
      // Ribbon — five metres of eel with a dorsal fin running nearly the whole
      // way. The body carries a standing wave so it reads as swimming before the
      // game touches it, and the tail is deliberately nothing: on this animal
      // the fin is the silhouette.
      const flat = 0.36;
      const at = (t: number) => -2.6 + t * 5.0;
      const wave = (t: number) => Math.sin(t * 6.4 - 1.0) * 0.11;
      // The widest station sits right behind the skull, and everything aft of it
      // is one long taper — the profile that separates an eel from a torpedo.
      const prof = (t: number) => 0.165 * Math.sin(Math.pow(1 - t, 0.3) * Math.PI) + 0.018;
      sweptTubeXY(mb, (t) => [at(t), wave(t), prof(t)], flat, T(24), T(8));

      // Dorsal ribbon: one strip standing on the back, height rolling along the
      // body and fading to nothing at both ends. Doubled so it survives being
      // seen from the wrong side.
      const dsegs = T(22);
      const ph = rng.range(0, Math.PI * 2);
      for (const nz of [1, -1]) {
        const b = mb.vertexCount;
        for (let k = 0; k <= dsegs; k++) {
          const u = k / dsegs;
          const t = 0.05 + u * 0.88;
          const y0 = wave(t) + prof(t) * 0.9;
          const h = (0.1 + Math.sin(u * 9.1 + ph) * 0.055) * Math.sin(Math.pow(u, 0.7) * Math.PI);
          mb.vertex(at(t), y0, 0, 0, 0, nz, u, 0);
          mb.vertex(at(t), y0 + h, 0, 0, 0, nz, u, 1);
        }
        for (let k = 0; k < dsegs; k++) {
          const q = b + k * 2;
          if (nz > 0) mb.quad(q, q + 2, q + 3, q + 1);
          else mb.quad(q, q + 1, q + 3, q + 2);
        }
      }
      // Tiny caudal, and pectorals barely bigger than the eyes.
      finFan(mb, at(0.01), wave(0.01), 0.13, 0.16, Math.PI * 0.45, Math.PI * 1.55, 6);
      for (const z of [-0.05, 0.05]) {
        mb.transform(1.75, wave(0.87) - 0.08, z, Math.PI / 2, 0, 0.4, 1, 1, 1);
        plate(mb, 0.14, 0.05);
        mb.popTransform();
      }
      if (!lo) {
        mb.material(MAT.glow);
        for (const z of [-0.07, 0.07]) {
          mb.transform(2.15, wave(0.95) + 0.06, z, 0, 0, 0, 0.055);
          sphere(mb, 1, 5, 4);
          mb.popTransform();
        }
      }
      break;
    }

    default: {
      // Fan — a coin on edge. The body is barely longer than it is deep and most
      // of the outline is fin, which is what picks it out of a shoal of three
      // fish that are all longer than they are tall.
      const flat = 0.3;
      const at = (t: number) => -0.78 + t * 1.7;
      const prof = (t: number) =>
        0.62 * Math.pow(Math.sin(Math.pow(1 - t, 0.95) * Math.PI), 0.55) + 0.03;
      mb.transform(0, 0, 0, 0, 0, 0, 1, 1, flat);
      revolveX(mb, (t) => [at(t), prof(t)], T(13), T(10));
      mb.popTransform();

      // Dorsal and anal sails, each about as tall again as the body is deep.
      const sail = rng.range(0.92, 1.0);
      finFan(mb, -0.05, 0.3, 0.62, 0.95 * sail, 0.35, Math.PI - 0.45, T(10), 0.06);
      mb.transform(0, 0, 0, Math.PI, 0, 0, 1, 1, 1);
      finFan(mb, -0.14, 0.26, 0.55, 0.84 * sail, 0.4, Math.PI - 0.5, T(10), 0.06);
      mb.popTransform();
      // Small fan tail on a short peduncle, and rounded pectorals.
      finFan(mb, -0.78, 0, 0.37, 0.38, Math.PI * 0.4, Math.PI * 1.6, T(8), 0.05);
      for (const z of [-0.13, 0.13]) {
        mb.transform(0.24, -0.3, z, Math.PI / 2, 0, 0.45, 1, 1, 1);
        plate(mb, 0.2, 0.11);
        mb.popTransform();
      }
      // Snout: a short wedge off the front of the disc, hinged down a fraction.
      mb.transform(0.88, -0.05, 0, 0, 0, -0.22, 1, 1, 1);
      box(mb, 0.15, 0.05, 0.08);
      mb.popTransform();
      // Prominent eye, set high and well forward on the disc.
      if (!lo) {
        mb.material(MAT.glow);
        for (const z of [-0.11, 0.11]) {
          mb.transform(0.6, 0.22, z, 0, 0, 0, 0.13, 0.13, 0.11);
          sphere(mb, 1, 7, 5);
          mb.popTransform();
        }
      }
      break;
    }
  }
  return mb.build();
}

/**
 * A torus in the XY plane, built from tangential cylinder segments.
 *
 * `cylinderY` runs along local Y and is centred on it, so a segment rotated by
 * its own polar angle has its length running AROUND the ring. Rotating by
 * `a + PI/2` instead — which is the intuitive-looking thing to write — points
 * every segment outward and builds a cog, not a ring. That mistake shipped
 * once and looked exactly like a cog.
 */
function ringXY(mb: MeshBuilder, radius: number, tube: number, seg = 40) {
  const len = (Math.PI * 2 * radius) / seg * 1.35;   // overlap, so no gaps show
  for (let k = 0; k < seg; k++) {
    const a = (k / seg) * Math.PI * 2;
    mb.transform(Math.cos(a) * radius, Math.sin(a) * radius, 0, 0, 0, a, 1, 1, 1);
    cylinderY(mb, tube, tube, len, 6, false);
    mb.popTransform();
  }
}

/**
 * A gate: the way into one of the rooms cut behind the world.
 *
 * Three meshes, because the thing has to MOVE and geometry cannot — the arch
 * stands still, the veil spins, shards orbit. Everything animated is animated
 * by the instance transform, which is free and is the only tool a static mesh
 * has.
 *
 * The hero is the glowing ring, not the stonework. That is what the station
 * gets right and what the first attempt at this got wrong: a bright simple
 * silhouette that blooms, with the structure a dark frame around it rather
 * than the loudest thing in the frame.
 */
export function buildGate(): MeshData {
  const mb = new MeshBuilder();
  const R = 4.3;

  // Stone first, and deliberately understated: a plain arch behind the light.
  mb.material(MAT.organic);
  ringXY(mb, R + 0.62, 0.42, 26);
  // Four keystones on the diagonals, tangential so they sit IN the arch.
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * Math.PI * 2 + Math.PI / 4;
    mb.transform(Math.cos(a) * (R + 0.62), Math.sin(a) * (R + 0.62), 0, 0, 0, a, 1, 1, 1);
    box(mb, 0.30, 0.66, 0.62);
    mb.popTransform();
  }

  // The light. Two concentric rings and a scatter of beads, all glow material,
  // which is what the bloom chain actually picks up.
  mb.material(MAT.glow);
  ringXY(mb, R, 0.30, 44);
  ringXY(mb, R - 0.78, 0.14, 36);
  for (let k = 0; k < 12; k++) {
    const a = (k / 12) * Math.PI * 2 + 0.13;
    mb.transform(Math.cos(a) * (R + 0.62), Math.sin(a) * (R + 0.62), 0.34, 0, 0, 0, 1, 1, 1);
    sphere(mb, 0.16, 6, 4);
    mb.popTransform();
  }
  return mb.build();
}

/**
 * The surface across a gate: nested rings that the draw spins.
 *
 * Rings rather than a disc, because a disc turned about its own normal does
 * not appear to turn at all. Two copies at different rates beat against each
 * other and the interference is the whole effect.
 */
export function buildGateVeil(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.glow);
  const RINGS = 5;
  for (let i = 0; i < RINGS; i++) {
    const t = (i + 1) / RINGS;
    // Thinner as they go out, so the middle reads as the brightest part.
    ringXY(mb, t * 3.4, 0.10 - t * 0.045, 20 + i * 6);
  }
  // A few chords across the middle, so the centre is not a hole.
  for (let k = 0; k < 5; k++) {
    const a = (k / 5) * Math.PI;
    mb.transform(0, 0, 0, 0, 0, a, 1, 1, 1);
    box(mb, 0.055, 3.3, 0.055);
    mb.popTransform();
  }
  return mb.build();
}

/** A shard hanging near a gate, torn loose from whatever it is made of. */
export function buildGateShard(): MeshData {
  const mb = new MeshBuilder();
  mb.material(MAT.glow);
  mb.transform(0, 0, 0, 0, 0, 0, 0.26, 0.9, 0.26);
  sphere(mb, 1, 4, 2);
  mb.popTransform();
  return mb.build();
}

/**
 * A MATERIAL TEST BALL.
 *
 * One sphere, one material tag, and deliberately nothing else on it — no
 * relief, no baked occlusion, no silhouette that reads as an object. Every
 * other mesh in this game is doing three things at once, so a shading change
 * lands on a rock that is also a normal map that is also a detail layer, and a
 * regression hides inside whichever of the three you were not looking at.
 *
 * A sphere covers every normal direction at once, which is the property that
 * matters here: one glance says what the lighting does facing the sun, facing
 * away, and at every grazing angle in between. That is the whole reason the
 * industry shoots a chrome ball on set.
 */
export function buildProbe(mat: number): MeshData {
  const mb = new MeshBuilder();
  mb.material(mat);
  /**
   * 128 x 96, NOT 28 x 18, AND A MIRROR IS WHY.
   *
   * The normals here are already smooth — `sphere` writes the exact analytic
   * normal per vertex and `quad` shares them — so this looked like enough, and
   * for the diffuse balls it is. What it misses is that interpolation makes the
   * normal field C0 and not C1: the direction is continuous across a quad edge,
   * its RATE OF CHANGE is not. Diffuse shading integrates over a hemisphere and
   * never notices. A mirror differentiates — reflection turns at twice the
   * normal's rate — so every quad boundary shows as a crease, and the sphere
   * reads as a faceted ball of trapezoids converging on the pole.
   *
   * It is invisible on PAINT and RUBBER and unmissable on CHROME, which is
   * exactly the pattern that made this look like a texture or resolution
   * problem for several rounds. It is neither, and it does not move when the
   * render scale does, because it is geometry.
   *
   * The number comes from the requirement rather than from taste. A facet
   * spans 360/segments degrees; it stops showing once that is inside the
   * specular lobe, and CHROME at 0.05 roughness has a lobe about 3 degrees
   * wide. 360/3 is 120, so 128.
   *
   * 24,576 triangles a ball against 1,008. Eight instances is ~197k, which
   * roughly doubles a light frame — paid only while the chart is up, on a debug
   * overlay whose entire job is to be looked at closely.
   */
  sphere(mb, 1, 128, 96);
  return mb.build();
}

/**
 * THE SPLASH CROWN, as geometry rather than as a puff of billboards.
 *
 * A hull crossing the surface throws BROKEN SHEETS of water outward. One
 * continuous funnel read as a translucent cone, so this mesh deliberately
 * leaves gaps between short, ragged arcs. Particles only supply the droplets;
 * the persistent spreading disturbance lives in the wake field.
 *
 * A UNIT crown, one metre in every direction, so the caller's scale IS the
 * splash's size in metres and nothing here has to know how hard the boat hit.
 *
 * WHY IT IS RAGGED AND NOT A CONE. A cone reads as a cone at any scale. Real
 * sheet water is unstable along its rim — surface tension pulls the edge into
 * ligaments at wavelengths it chooses itself — so the rim height is modulated
 * by two incommensurate harmonics around the ring. Every finger is then a
 * different height, none of the pattern repeats within one turn, and the
 * silhouette never resolves into a shape you can name.
 *
 * The wall LEANS OUT with height, which is the second thing a photograph shows
 * and a cone does not: water leaving a hole is moving outward as well as up,
 * so the sheet is a hyperboloid, wider at the lip than the flare alone would
 * make it.
 *
 * Two rings of quads let each surviving arc curl outward. Even before the gaps,
 * 24 segments is only 96 triangles.
 */
export function buildCrown(seg = 24): MeshData {
  const mb = new MeshBuilder();
  // Its own slot, because a sheet of aerated water is not any of the others
  // and because this mesh draws through the TRANSLUCENT pass, where the
  // material tag is what selects the shading. See `MAT.foam`.
  mb.material(MAT.foam);
  const TAU = Math.PI * 2;
  // Base radius 1 by definition; the lip flares to this.
  const LIP = 1.24;
  // Where the middle loop sits, as a fraction of height and of the flare. Below
  // half, so the sheet is steep at the bottom and turns over near the top —
  // which is the profile that reads as thrown rather than poured.
  const MID_H = 0.34, MID_R = 1.09;

  const ring = (t: number, r: number, y: number) => {
    const a = t * TAU;
    // Outward and slightly up: the sheet is nearly vertical, so its normal is
    // nearly horizontal, and the small upward tilt is what catches the sky.
    const nx = Math.cos(a), nz = Math.sin(a);
    const l = Math.hypot(nx, 0.34, nz);
    return mb.vertex(nx * r, y, nz * r, nx / l, 0.34 / l, nz / l, t, y);
  };

  for (let i = 0; i < seg; i++) {
    const t0 = i / seg, t1 = (i + 1) / seg;
    // Five broad lobes with an eleven-wave tear make irregular arcs while
    // keeping their gaps stable for the short life of the instance.
    const tm = (t0 + t1) * 0.5;
    const open = Math.sin(tm * TAU * 5.0 + 0.4)
      + Math.sin(tm * TAU * 11.0 + 1.7) * 0.34;
    if (open < -0.12) continue;
    const h = (t: number) => 0.88
      + Math.sin(t * TAU * 5.0 + 0.4) * 0.16
      + Math.sin(t * TAU * 9.0 + 2.1) * 0.09;
    const b0 = ring(t0, 1.0, 0.0), b1 = ring(t1, 1.0, 0.0);
    const m0 = ring(t0, MID_R, MID_H * h(t0)), m1 = ring(t1, MID_R, MID_H * h(t1));
    const r0 = ring(t0, LIP, h(t0)), r1 = ring(t1, LIP, h(t1));
    mb.quad(b0, b1, m1, m0);
    mb.quad(m0, m1, r1, r0);
  }
  return mb.build();
}
